import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Send, Beaker, Atom, Dna, Globe, BookOpen, Lightbulb, FileText, BarChart3, TestTube, FlaskConical, Download, X, AlertCircle, Wifi, WifiOff, Lock, LogOut, Clock, User, Key } from 'lucide-react';
// Firebase 설정 추가
import { initializeApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
// 🔥 실제 Excel 파일 읽기를 위한 XLSX 추가
import * as XLSX from 'xlsx';

// Firebase 프로젝트 설정
const firebaseConfig = {
  projectId: "littlescienceaichat",
  // 다른 설정은 Functions만 사용할 때는 불필요
};

// Firebase 초기화
const app = initializeApp(firebaseConfig);
const functions = getFunctions(app, 'asia-northeast3');

// AI 모드 감지 (URL 파라미터: ?mode=natural 또는 ?mode=classic)
const getAIMode = () => {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('mode') || 'classic';
  }
  return 'classic';
};

// 문서 내용 요약 함수 (natural 모드에서 문서 요약 참조용)
const summarizeDocument = (html, maxLength = 800) => {
  if (!html) return null;
  const textContent = html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<[^>]+>/g, '\n')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\n\s*\n/g, '\n')
    .trim();
  const lines = textContent.split('\n').filter(line => line.trim());
  let summary = [];
  let currentLength = 0;
  for (const line of lines) {
    const trimmedLine = line.trim();
    if (currentLength + trimmedLine.length > maxLength) break;
    if (trimmedLine.length > 3) {
      summary.push(trimmedLine);
      currentLength += trimmedLine.length;
    }
  }
  return summary.slice(0, 15).join('\n');
};

export default function ScienceResearchAI() {
  // ===== 🔥 개선된 인증 관련 상태 =====
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [authError, setAuthError] = useState('');
  const [sessionInfo, setSessionInfo] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0); // 남은 시간 (초)
  const [userStatus, setUserStatus] = useState('guest'); // 'guest', 'trial', 'premium', 'unlimited'
  const [isTimerActive, setIsTimerActive] = useState(false);
  
  // 🔥 추가된 이용권 상세 정보
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [lastLicenseCheck, setLastLicenseCheck] = useState(0);

  // ===== 기존 상태들 (그대로 유지) =====
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: '안녕! 과학 실험이나 연구 아이디어 있으면 편하게 말해봐! 🧪\n\n어떤 분야에 관심있어? 화학, 생물, 물리, 환경과학... 뭐든 좋아!',
      timestamp: new Date().toLocaleTimeString(),
      isComplete: true
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentResearchTopic, setCurrentResearchTopic] = useState(null);
  const [researchStage, setResearchStage] = useState('exploration');
  
  // 스마트 트리거 상태
  const [conversationDepth, setConversationDepth] = useState(0); // eslint-disable-line no-unused-vars
  const [planningButtonEnabled, setPlanningButtonEnabled] = useState(false);
  const [reportButtonEnabled, setReportButtonEnabled] = useState(false);
  
  // 타이핑 애니메이션 상태
  const [typingMessageIndex, setTypingMessageIndex] = useState(null);
  const [displayedText, setDisplayedText] = useState('');
  
  // 문서 생성 상태
  const [documentMode, setDocumentMode] = useState(null);
  const [isDocumentGenerating, setIsDocumentGenerating] = useState(false);
  const [documentContent, setDocumentContent] = useState('');
  const [documentProgress, setDocumentProgress] = useState(0);
  const [documentProgressMessage, setDocumentProgressMessage] = useState('');
  const [isPrintMode, setIsPrintMode] = useState(false); // eslint-disable-line no-unused-vars
  
  // 토큰 사용량 추적
  const [tokenUsage, setTokenUsage] = useState({
    chatMessages: 0,
    systemPrompts: 0,
    generatedDocuments: 0,
    contextOverhead: 0,
    apiCallCount: 0
  });
  
  // 🔥 실제 Excel 데이터 상태 추가
  const [realExcelData, setRealExcelData] = useState({ isef: [], domestic: [] });
  const [isExcelLoaded, setIsExcelLoaded] = useState(false);
  const [excelLoadError, setExcelLoadError] = useState('');
  
  // 🆕 연결 상태 추가
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [retryCount, setRetryCount] = useState(0);

  // AI 모드 (classic 또는 natural) - URL 파라미터 기반
  const aiMode = useMemo(() => getAIMode(), []);
  
  const messagesEndRef = useRef(null);
  const maxTokens = 20000;

  // ===== 🆕 개선된 인증 관련 함수들 =====
  
  // 🔥 이용권 상세 정보 처리
  const processLicenseInfo = useCallback((sessionData) => {
    const currentTime = new Date();
    
    let licenseType = 'unknown';
    let expireTime = null;
    let timeLeftSeconds = 0;
    let isExpired = false;
    
    if (sessionData.code === '0000') {
      // 무료체험
      licenseType = 'trial';
      expireTime = new Date(currentTime.getTime() + (sessionData.timeLeft * 1000));
      timeLeftSeconds = sessionData.timeLeft;
      isExpired = timeLeftSeconds <= 0;
    } else if (sessionData.type === 'unlimited') {
      // 무제한 이용권
      licenseType = 'unlimited';
      expireTime = null;
      timeLeftSeconds = Infinity;
      isExpired = false;
    } else if (sessionData.remainingTime !== undefined) {
      // 🆕 백엔드에서 remainingTime을 직접 제공하는 경우 (우선 사용)
      licenseType = sessionData.duration_days > 0 ? 'premium_days' : 'premium_minutes';
      timeLeftSeconds = sessionData.remainingTime;
      if (sessionData.expireDate) {
        expireTime = new Date(sessionData.expireDate);
      }
      isExpired = timeLeftSeconds <= 0;
    } else if (sessionData.duration_days) {
      // 일 단위 이용권 (폴백)
      licenseType = 'premium_days';
      const startTime = new Date(sessionData.startTime || currentTime);
      expireTime = new Date(startTime.getTime() + (sessionData.duration_days * 24 * 60 * 60 * 1000));
      timeLeftSeconds = Math.max(0, Math.floor((expireTime - currentTime) / 1000));
      isExpired = timeLeftSeconds <= 0;
    } else if (sessionData.duration_minutes) {
      // 분 단위 이용권 (폴백)
      licenseType = 'premium_minutes';
      const startTime = new Date(sessionData.startTime || currentTime);
      expireTime = new Date(startTime.getTime() + (sessionData.duration_minutes * 60 * 1000));
      timeLeftSeconds = Math.max(0, Math.floor((expireTime - currentTime) / 1000));
      isExpired = timeLeftSeconds <= 0;
    }
    
    const licenseInfo = {
      type: licenseType,
      code: sessionData.code,
      startTime: sessionData.startTime ? new Date(sessionData.startTime) : currentTime,
      expireTime: expireTime,
      timeLeft: timeLeftSeconds,
      isExpired: isExpired,
      status: isExpired ? 'expired' : 'active',
      displayName: getLicenseDisplayName(licenseType, sessionData),  // 🔧 sessionData 추가 전달
      statusIcon: getLicenseStatusIcon(licenseType, isExpired, timeLeftSeconds)
    };
    
    setLicenseInfo(licenseInfo);
    setTimeLeft(timeLeftSeconds);
    
    // 사용자 상태 업데이트
    if (isExpired) {
      setUserStatus('expired');
      setIsTimerActive(false);
    } else if (licenseType === 'trial') {
      setUserStatus('trial');
      setIsTimerActive(true);
    } else if (licenseType === 'unlimited') {
      setUserStatus('unlimited');
      setIsTimerActive(false);
    } else {
      setUserStatus('premium');
      setIsTimerActive(timeLeftSeconds < Infinity);
    }
    
    return licenseInfo;
  }, []);
  
  // 🔧 수정: sessionData도 매개변수로 받아서 productName 활용
  const getLicenseDisplayName = useCallback((licenseType, sessionData = null) => {
    // 🆕 백엔드에서 제공하는 productName 우선 사용
    if (sessionData?.productName) {
      return sessionData.productName; // "30일 이용권" 등 정확한 명칭 사용
    }
    
    switch (licenseType) {
      case 'trial': return '무료체험';
      case 'unlimited': return '무제한 이용권';
      case 'premium_days': return '프리미엄 (일)';
      case 'premium_minutes': return '프리미엄 (분)';
      default: return '알 수 없음';
    }
  }, []);
  
  // 이용권 상태별 아이콘 반환
  const getLicenseStatusIcon = useCallback((licenseType, isExpired, timeLeftSeconds) => {
    if (isExpired) return '❌';
    if (licenseType === 'unlimited') return '♾️';
    if (licenseType === 'trial' && timeLeftSeconds <= 60) return '⚠️';
    if (timeLeftSeconds <= 3600) return '⏰'; // 1시간 이하
    return '✅';
  }, []);
  
  // 시간 포맷팅 개선
  const formatTimeLeft = useCallback((seconds) => {
    if (seconds === Infinity) return '무제한';
    if (seconds <= 0) return '만료됨';
    
    const days = Math.floor(seconds / (24 * 60 * 60));
    const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((seconds % (60 * 60)) / 60);
    const secs = seconds % 60;
    
    if (days > 0) {
      return `${days}일 ${hours}시간 ${minutes}분`;
    } else if (hours > 0) {
      return `${hours}시간 ${minutes}분`;
    } else if (minutes > 0) {
      return `${minutes}분 ${secs}초`;
    } else {
      return `${secs}초`;
    }
  }, []);
  
  // 🔧 수정 1: sessionData 저장 함수 추가
  const saveSessionData = useCallback((responseData) => {
    try {
      // 🚀 기존 방식 그대로 유지 (호환성 보장)
      localStorage.setItem('accessSession', JSON.stringify(responseData));
      
      // 🆕 새로운 sessionData 별도 저장 (백엔드 요구사항)
      if (responseData.sessionData) {
        localStorage.setItem('sessionData', JSON.stringify(responseData.sessionData));
        console.log('✅ sessionData 별도 저장 완료:', responseData.sessionData);
      }
    } catch (error) {
      console.error('❌ sessionData 저장 실패:', error);
    }
  }, []);

  // 🔧 수정 2: sessionData 조회 함수 추가
  const getStoredSessionData = useCallback(() => {
    try {
      // 1순위: 새로운 sessionData
      const sessionDataStr = localStorage.getItem('sessionData');
      if (sessionDataStr) {
        const sessionData = JSON.parse(sessionDataStr);
        console.log('📄 저장된 sessionData 사용:', sessionData);
        return sessionData;
      }
      
      // 2순위: 기존 accessSession에서 sessionData 추출
      const accessSessionStr = localStorage.getItem('accessSession');
      if (accessSessionStr) {
        const accessSession = JSON.parse(accessSessionStr);
        if (accessSession.sessionData) {
          console.log('📄 accessSession에서 sessionData 추출:', accessSession.sessionData);
          return accessSession.sessionData;
        }
        // 3순위: accessSession 자체를 sessionData로 사용 (기존 호환성)
        console.log('📄 accessSession을 sessionData로 사용:', accessSession);
        return accessSession;
      }
      
      console.log('⚠️ 저장된 sessionData 없음');
      return null;
    } catch (error) {
      console.error('❌ sessionData 파싱 오류:', error);
      return null;
    }
  }, []);
  
  // 🔥 개선된 이용권 유효성 체크 - Firebase Functions 응답 처리 수정
  const checkLicenseValidity = useCallback(async (code, skipCache = false) => {
    const now = Date.now();
    
    // 🔥 캐시 체크 (30초마다만 서버 확인)
    if (!skipCache && sessionInfo && (now - lastLicenseCheck) < 30000) {
      console.log('🎯 캐시된 이용권 정보 사용');
      return { isValid: true, licenseInfo, message: '캐시된 정보 사용' };
    }
    
    console.log('🔑 서버에서 이용권 체크 중:', code);
    
    try {
      // 🚀 무료체험 코드도 이제 백엔드로 전송
      // 🚀 무료체험 코드도 이제 백엔드로 전송
if (code === '0000') {
  // 🆕 localStorage 기반 1차 체크
  const trialRecord = localStorage.getItem('trialCompleted');
  if (trialRecord) {
    try {
      const parsed = JSON.parse(trialRecord);
      if (parsed.completed) {
        return {
          isValid: false,
          licenseInfo: null,
          message: '이 기기에서는 이미 무료체험을 완료하셨습니다. 다른 기기에서 시도해주세요.'
        };
      }
    } catch (error) {
      console.error('trialRecord 파싱 오류:', error);
    }
  }
  
  console.log('🎯 무료체험 코드 - 백엔드 IP 체크');
  
  const verifyFreeTrial = httpsCallable(functions, 'verifyFreeTrial');
  const result = await verifyFreeTrial({ code: '0000' });
        
        const data = result.data;
        console.log('📦 무료체험 서버 응답:', data);
        
        if (data.success) {
          const processedInfo = processLicenseInfo(data.sessionData);
          setLastLicenseCheck(now);
          
          return {
            isValid: true,
            licenseInfo: processedInfo,
            message: data.message
          };
        } else {
          return {
            isValid: false,
            licenseInfo: null,
            message: data.message
          };
        }
      }
      
      // 🔥 유료 코드만 백엔드로 전송 - Firebase Functions 응답 처리 수정
      const verifyCode = httpsCallable(functions, 'verifyAccessCode');
      const result = await verifyCode({ code: code });
      
      // ⚡ 중요: Firebase Functions는 응답을 result.data 안에 넣습니다!
      const data = result.data;
      console.log('📦 서버 응답 데이터:', data);
      
      // 🚀 수정 3: 서버 응답을 localStorage에 저장
      saveSessionData(data);
      
      setLastLicenseCheck(now);
      
      if (data.success) {
        // 🔥 서버 응답 데이터 구조에 맞게 수정
        const sessionData = {
          code: code,
          type: data.accessType === 'premium' ? 'premium' : data.accessType,
          duration_days: data.validDays || 0,  // 🔧 validDays → duration_days
          duration_minutes: data.validMinutes || 0,  // 🔧 validMinutes → duration_minutes
          duration: data.duration || '',
          productName: data.productName || data.ticketType || '',
          startTime: data.startDate || new Date().toISOString(),
          expireDate: data.expireDate || null,
          accessType: data.accessType || 'unknown',
          remainingTime: data.remainingTime || 0  // 🆕 remainingTime 추가
        };
        
        console.log('✅ 처리할 세션 데이터:', sessionData);
        
        const processedInfo = processLicenseInfo(sessionData);
        
        // 🔧 디버깅: 처리된 라이센스 정보 확인
        console.log('📄 처리된 라이센스 정보:', processedInfo);
        console.log('📄 라이센스 displayName:', processedInfo?.displayName);
        console.log('📄 라이센스 type:', processedInfo?.type);
        
        if (processedInfo.isExpired) {
          return {
            isValid: false,
            licenseInfo: processedInfo,
            message: `이용권이 만료되었습니다. (만료일: ${processedInfo.expireTime?.toLocaleString()})`
          };
        }
        
        return {
          isValid: true,
          licenseInfo: processedInfo,
          message: `${processedInfo.displayName} 인증 완료!`
        };
      } else {
        return {
          isValid: false,
          licenseInfo: null,
          message: data.message || '잘못된 액세스 코드입니다.'
        };
      }
    } catch (error) {
      console.error('❌ 이용권 체크 오류:', error);
      return {
        isValid: false,
        licenseInfo: null,
        message: '네트워크 오류가 발생했습니다. 다시 시도해주세요.'
      };
    }
  }, [sessionInfo, lastLicenseCheck, licenseInfo, processLicenseInfo, saveSessionData]);
  
  // 액세스 코드 검증 (개선된 버전) - Firebase Functions 응답 처리 수정
  const verifyAccessCode = useCallback(async (code) => {
  setIsVerifying(true);
  setAuthError('');
  
  // 🆕 무료체험 코드 사전 체크
  if (code === '0000') {
    const trialRecord = localStorage.getItem('trialCompleted');
    if (trialRecord) {
      try {
        const parsed = JSON.parse(trialRecord);
        if (parsed.completed) {
          setAuthError('이 기기에서는 이미 무료체험을 완료하셨습니다.');
          setIsVerifying(false);
          return;
        }
      } catch (error) {
        console.error('trialRecord 체크 오류:', error);
      }
    }
  }
  
  try {
    console.log('🔑 액세스 코드 검증 중:', code);
      
      const result = await checkLicenseValidity(code, true); // 강제 서버 체크
      
      if (result.isValid) {
        setIsAuthenticated(true);
        setSessionInfo({ 
          code: code, 
          ...result.licenseInfo 
        });
        
        // 환영 메시지 생성
        const welcomeMessage = {
          role: 'assistant',
          content: generateWelcomeMessage(result.licenseInfo),
          timestamp: new Date().toLocaleTimeString(),
          isComplete: true
        };
        setMessages(prev => [...prev, welcomeMessage]);
        
        console.log('✅ 인증 성공!', result.licenseInfo);
      } else {
        setAuthError(result.message);
      }
    } catch (error) {
      console.error('❌ 코드 검증 오류:', error);
      setAuthError('네트워크 오류가 발생했습니다. 다시 시도해주세요.');
    }
    
    setIsVerifying(false);
  }, [checkLicenseValidity]);
  
  // 🔥 이용권 타입별 환영 메시지 생성
  const generateWelcomeMessage = useCallback((licenseInfo) => {
    const icon = licenseInfo.statusIcon;
    const displayName = licenseInfo.displayName;
    
    switch (licenseInfo.type) {
      case 'trial':
        return `🎉 **${displayName} 시작!** ${icon}\n\n6분 동안 모든 기능을 자유롭게 사용해보세요!\n\n• 🔬 연구계획서 생성\n• 📝 실험보고서 작성\n• 🏆 대회 출품작 AI 검색\n• 💬 과학 상담 및 실험 설계\n\n우측 상단에서 남은 시간을 확인할 수 있어요! ⏰`;
      
      case 'unlimited':
        return `♾️ **${displayName} 인증 완료!**\n\n영구적으로 모든 기능을 무제한 사용하실 수 있습니다!\n\n• ⭐ 무제한 대화 및 문서 생성\n• 🔬 고급 연구계획서 템플릿\n• 📊 상세 실험보고서 작성\n• 🎯 맞춤형 과학 실험 설계\n• 🏆 대회 출품작 무제한 검색\n\n영구 이용권으로 과학 연구의 모든 가능성을 탐험해보세요! 🚀`;
      
      case 'premium_days':
      case 'premium_minutes':
        const timeInfo = licenseInfo.timeLeft === Infinity ? '무제한' : formatTimeLeft(licenseInfo.timeLeft);
        return `✨ **${displayName} 인증 완료!** ${icon}\n\n남은 시간: **${timeInfo}**\n\n프리미엄 기능을 모두 사용하실 수 있습니다!\n\n• ⭐ 확장된 대화 및 문서 생성\n• 🔬 고급 연구계획서 템플릿\n• 📊 상세 실험보고서 작성\n• 🎯 맞춤형 과학 실험 설계\n• 🏆 대회 출품작 검색\n\n프리미엄 사용자로 과학 연구의 모든 가능성을 탐험해보세요! 🚀`;
      
      default:
        return `✅ **인증 완료!**\n\n모든 기능을 사용하실 수 있습니다!\n\n• 🔬 연구계획서 생성\n• 📝 실험보고서 작성\n• 🏆 대회 출품작 검색\n• 💬 과학 상담`;
    }
  }, [formatTimeLeft]);

  // 🔧 수정 4: 세션 상태 확인 함수에 checkSessionStatus 호출 추가
  const checkSession = useCallback(async () => {
    if (!isAuthenticated || !sessionInfo) return;
    
    console.log('📄 세션 상태 확인 중...');
    
    try {
      // 🚀 무료체험(0000)은 백엔드 체크 안 함
      if (sessionInfo.code === '0000') {
        console.log('🎯 무료체험 - 백엔드 세션 체크 스킵');
        return;
      }
      
      // 🆕 추가: checkSessionStatus 호출
      const storedSessionData = getStoredSessionData();
      if (storedSessionData) {
        console.log('📡 checkSessionStatus 호출 중...', storedSessionData);
        
        const checkSessionStatus = httpsCallable(functions, 'checkSessionStatus');
        const result = await checkSessionStatus({ sessionData: storedSessionData });
        
        const data = result.data;
        console.log('📊 checkSessionStatus 응답:', data);
        
        if (!data.success) {
          console.log('🚨 세션 만료 감지');
          handleSessionExpired();
          return;
        }
        
        // 세션이 유효한 경우 남은 시간 업데이트
        if (data.remainingTime !== undefined) {
          setTimeLeft(data.remainingTime);
          console.log('✅ 세션 상태 업데이트됨, 남은 시간:', data.remainingTime);
        }
      } else {
        console.log('⚠️ 저장된 sessionData 없음 - 기존 방식으로 체크');
        const result = await checkLicenseValidity(sessionInfo.code);
        
        if (!result.isValid) {
          console.log('🚨 세션 만료 감지');
          if (result.licenseInfo?.isExpired) {
            handleTrialExpired();
          } else {
            handleSessionExpired();
          }
        } else {
          // 이용권 정보 업데이트
          if (result.licenseInfo) {
            const updatedInfo = processLicenseInfo(result.licenseInfo);
            console.log('✅ 세션 정보 업데이트됨');
          }
        }
      }
    } catch (error) {
      console.error('세션 확인 오류:', error);
      // 네트워크 오류는 무시하고 로컬 타이머 계속 사용
    }
  }, [isAuthenticated, sessionInfo, getStoredSessionData, checkLicenseValidity, processLicenseInfo]);

  // 🔥 무료체험 사용시간 업데이트 함수 (새로 추가)
  const updateTrialUsage = useCallback(async (usedSeconds) => {
  if (sessionInfo?.code === '0000' && usedSeconds > 0) {
    try {
      const updateUsage = httpsCallable(functions, 'updateFreeTRialUsage');
      await updateUsage({ 
        usedSeconds,
        completed: usedSeconds >= 360, // 🆕 완료 여부 추가
        finalUpdate: usedSeconds >= 360 // 🆕 최종 업데이트 플래그
      });
      console.log(`📊 무료체험 사용시간 업데이트: ${usedSeconds}초`);
    } catch (error) {
      console.error('사용시간 업데이트 실패:', error);
    }
  }
}, [sessionInfo]);

  // 로그아웃 함수 (개선된 버전)
  const handleLogout = useCallback(() => {
    console.log('🚪 로그아웃 프로세스 시작');
    
    // 🆕 무료체험 사용 중이면 사용시간 업데이트
    if (sessionInfo?.code === '0000' && timeLeft < 360) {
      const usedTime = 360 - timeLeft;
      updateTrialUsage(Math.max(usedTime, 0));
    }
    
    setIsAuthenticated(false);
    setAccessCode('');
    setSessionInfo(null);
    setLicenseInfo(null);
    setTimeLeft(0);
    setUserStatus('guest');
    setIsTimerActive(false);
    setAuthError('');
    setLastLicenseCheck(0);
    
    // 🚀 localStorage도 정리
    localStorage.removeItem('accessSession');
    localStorage.removeItem('sessionData');
    
    console.log('📄 상태 초기화 완료');
    
    // 대화 리셋
    setMessages([
      {
        role: 'assistant',
        content: '안녕! 과학 실험이나 연구 아이디어 있으면 편하게 말해봐! 🧪\n\n어떤 분야에 관심있어? 화학, 생물, 물리, 환경과학... 뭐든 좋아!',
        timestamp: new Date().toLocaleTimeString(),
        isComplete: true
      }
    ]);
    
    // 다른 상태들 리셋
    setCurrentResearchTopic(null);
    setPlanningButtonEnabled(false);
    setReportButtonEnabled(false);
    setDocumentMode(null);
    setDocumentContent('');
    
    console.log('✅ 로그아웃 완료 - 로그인 화면으로 이동');
  }, [sessionInfo, timeLeft, updateTrialUsage]);

  // 무료체험 만료 처리 (개선된 버전)
  const handleTrialExpired = useCallback(() => {
  console.log('⏰ 무료체험 만료 처리 시작');
  
  // 🆕 무료체험 완료 기록 (localStorage)
  const trialRecord = {
    completed: true,
    completedAt: new Date().toISOString(),
    deviceFingerprint: navigator.userAgent + window.screen.width + window.screen.height
  };
  localStorage.setItem('trialCompleted', JSON.stringify(trialRecord));
  
  // 실제 사용한 시간 계산해서 서버에 업데이트
  const originalTime = 360;
  const usedTime = originalTime - timeLeft;
  updateTrialUsage(Math.max(usedTime, 0));
    
    setIsTimerActive(false);
    setTimeLeft(0);
    setUserStatus('expired');
    
    if (licenseInfo) {
      setLicenseInfo(prev => prev ? { ...prev, isExpired: true, status: 'expired' } : null);
    }
    
    const expiredMessage = {
      role: 'assistant',
      content: `⏰ **무료체험이 만료되었습니다!**\n\n6분간의 체험은 어떠셨나요? 😊\n\n**계속 사용하시려면:**\n• 유료 액세스 코드를 구매하거나\n• 다른 기기에서 무료체험을 다시 시작해보세요\n\n감사합니다! 🙏`,
      timestamp: new Date().toLocaleTimeString(),
      isComplete: true
    };
    setMessages(prev => [...prev, expiredMessage]);
    
    console.log('📝 만료 메시지 추가됨');
    
    // 3초 후 로그아웃
    setTimeout(() => {
      console.log('🚪 자동 로그아웃 실행');
      handleLogout();
    }, 3000);
  }, [timeLeft, updateTrialUsage, licenseInfo, handleLogout]);

  // 세션 만료 처리 (개선된 버전)
  const handleSessionExpired = useCallback(() => {
    console.log('🔒 세션 만료 처리 시작');
    
    const expiredMessage = {
      role: 'assistant',
      content: `🔒 **세션이 만료되었습니다.**\n\n다시 액세스 코드를 입력해주세요.`,
      timestamp: new Date().toLocaleTimeString(),
      isComplete: true
    };
    setMessages(prev => [...prev, expiredMessage]);
    
    setTimeout(() => {
      handleLogout();
    }, 2000);
  }, [handleLogout]);

  // ===== 기존 함수들 (완전 복원) =====
  
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // 토큰 수 추정 함수
  const estimateTokens = useCallback((text) => {
    return Math.ceil(text.length / 3.5);
  }, []);

  const calculateUsage = useCallback(() => {
    const chatTokens = messages.reduce((sum, msg) => 
      sum + estimateTokens(msg.content), 0
    );
    const systemTokens = estimateTokens("시스템프롬프트") * tokenUsage.apiCallCount;
    const documentTokens = documentContent ? estimateTokens(documentContent) : 0;
    const contextOverhead = chatTokens * tokenUsage.apiCallCount * 0.5;
    
    const totalTokens = chatTokens + systemTokens + documentTokens + contextOverhead;
    return Math.min((totalTokens / maxTokens) * 100, 100);
  }, [messages, estimateTokens, tokenUsage.apiCallCount, documentContent]);

  const getCurrentTokens = useCallback(() => {
    const chatTokens = messages.reduce((sum, msg) => 
      sum + estimateTokens(msg.content), 0
    );
    const systemTokens = estimateTokens("시스템프롬프트") * tokenUsage.apiCallCount;
    const documentTokens = documentContent ? estimateTokens(documentContent) : 0;
    const contextOverhead = chatTokens * tokenUsage.apiCallCount * 0.5;
    
    return chatTokens + systemTokens + documentTokens + contextOverhead;
  }, [messages, estimateTokens, tokenUsage.apiCallCount, documentContent]);

  const getUsageColor = useCallback(() => {
    const usage = calculateUsage();
    if (usage < 50) return 'bg-green-500';
    if (usage < 80) return 'bg-yellow-500';
    return 'bg-red-500';
  }, [calculateUsage]);

  // 🆕 연결 상태 체크 함수 - Firebase Functions 응답 처리 수정
  const checkConnectionStatus = useCallback(async () => {
    try {
      // 🔧 Firebase httpsCallable 방식으로 변경
      const checkSessionStatus = httpsCallable(functions, 'checkSessionStatus');
      const result = await checkSessionStatus({ test: true });
      
      // ⚡ 중요: Firebase Functions 응답 처리 수정
      if (result.data) {
        setConnectionStatus('connected');
        setRetryCount(0);
        return true;
      } else {
        setConnectionStatus('unstable');
        return false;
      }
    } catch (error) {
      console.warn("연결 상태 체크 실패:", error);
      setConnectionStatus('disconnected');
      return false;
    }
  }, []);
 // 🔒 이름 마스킹 함수 (개인정보 보호)
  const maskAuthorName = useCallback((authorName) => {
    if (!authorName || authorName === '작성자 없음' || authorName.trim() === '') {
      return '작성자 정보 없음';
    }

    // 여러 작성자가 있는 경우 (쉼표나 '&'로 구분)
    const authors = authorName.split(/[,&]/).map(name => name.trim());
    
    const maskedAuthors = authors.map(name => {
      if (!name) return '';
      
      // 한글 이름 처리 (성은 그대로, 이름은 마스킹)
      const koreanNameMatch = name.match(/^([가-힣])([가-힣]+)$/);
      if (koreanNameMatch) {
        const surname = koreanNameMatch[1];
        const givenNameLength = koreanNameMatch[2].length;
        return surname + '*'.repeat(givenNameLength);
      }
      
      // 영어 이름 처리 (첫 글자만, 나머지는 마스킹)
      const englishNameMatch = name.match(/^([A-Za-z])([A-Za-z]+)(.*)$/);
      if (englishNameMatch) {
        const firstLetter = englishNameMatch[1];
        const restLength = englishNameMatch[2].length;
        const suffix = englishNameMatch[3]; // 성이나 추가 정보
        return firstLetter + '*'.repeat(restLength) + (suffix ? ' ' + suffix.charAt(0) + '*'.repeat(Math.max(0, suffix.length-1)) : '');
      }
      
      // 기타 형태 (첫 글자만 표시)
      if (name.length > 1) {
        return name.charAt(0) + '*'.repeat(name.length - 1);
      }
      
      return name;
    });
    
    return maskedAuthors.filter(name => name).join(', ');
  }, []);


  // 🔥 실제 Excel 데이터 로딩 함수 (프로덕션용 수정) - 재시도 로직 강화
  const loadRealExcelData = useCallback(async () => {
    const maxRetries = 3;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`📊 Excel 데이터 로딩 시도 ${attempt}/${maxRetries}...`);
        
        // 🔧 window.fs.readFile → fetch로 변경
        // ISEF 데이터 로딩
        console.log("📥 ISEF 파일 다운로드 중...");
        const isefResponse = await fetch('/data/ISEF 국제대회.xlsx', {
          method: 'GET',
          cache: 'force-cache', // 캐시 활용
          headers: {
            'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          }
        });
        
        if (!isefResponse.ok) {
          throw new Error(`ISEF 파일 로딩 실패: ${isefResponse.status} - ${isefResponse.statusText}`);
        }
        
        const isefArrayBuffer = await isefResponse.arrayBuffer();
        console.log(`✅ ISEF 파일 크기: ${(isefArrayBuffer.byteLength / 1024 / 1024).toFixed(2)}MB`);
        
        const isefWorkbook = XLSX.read(isefArrayBuffer, {
          cellStyles: true,
          cellFormulas: true,
          cellDates: true
        });
        const isefSheet = isefWorkbook.Sheets[isefWorkbook.SheetNames[0]];
        const isefData = XLSX.utils.sheet_to_json(isefSheet);
        
        console.log("📥 국내DB 파일 다운로드 중...");
        // 국내 데이터 로딩
        const domesticResponse = await fetch('/data/국내DB.xlsx', {
          method: 'GET',
          cache: 'force-cache',
          headers: {
            'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          }
        });
        
        if (!domesticResponse.ok) {
          throw new Error(`국내DB 파일 로딩 실패: ${domesticResponse.status} - ${domesticResponse.statusText}`);
        }
        
        const domesticArrayBuffer = await domesticResponse.arrayBuffer();
        console.log(`✅ 국내DB 파일 크기: ${(domesticArrayBuffer.byteLength / 1024 / 1024).toFixed(2)}MB`);
        
        const domesticWorkbook = XLSX.read(domesticArrayBuffer, {
          cellStyles: true,
          cellFormulas: true,
          cellDates: true
        });
        const domesticSheet = domesticWorkbook.Sheets['국내학술'] || domesticWorkbook.Sheets[domesticWorkbook.SheetNames[0]];
        const domesticData = XLSX.utils.sheet_to_json(domesticSheet);
        
        // 🆕 데이터 검증
        if (isefData.length === 0 && domesticData.length === 0) {
          throw new Error('Excel 파일에서 데이터를 찾을 수 없습니다');
        }
        
        setRealExcelData({
          isef: isefData,
          domestic: domesticData
        });
        
        setIsExcelLoaded(true);
        setExcelLoadError('');
        console.log(`✅ Excel 데이터 로딩 완료! ISEF: ${isefData.length.toLocaleString()}개, 국내: ${domesticData.length.toLocaleString()}개`);
        
        // 성공 시 루프 종료
        return;
        
      } catch (error) {
        console.error(`❌ Excel 데이터 로딩 시도 ${attempt} 실패:`, error);
        
        if (attempt === maxRetries) {
          // 🔧 최종 실패 시 더 상세한 에러 메시지
          const detailedError = `Excel 로딩 최종 실패 (${maxRetries}회 시도): ${error.message}`;
          setExcelLoadError(detailedError);
          
          // 🔧 폴백: 빈 데이터로라도 설정해서 앱이 작동하도록
          setRealExcelData({
            isef: [],
            domestic: []
          });
          setIsExcelLoaded(true); // 에러여도 로딩 완료로 처리
          
          // 🆕 사용자에게 알림
          const errorMessage = {
            role: 'assistant',
            content: `⚠️ **Excel 데이터 로딩 실패**\n\n${detailedError}\n\n대회 출품작 검색 기능은 일시적으로 사용할 수 없지만, 다른 모든 기능은 정상 작동합니다!\n\n• 연구계획서/실험보고서 생성 ✅\n• 과학 실험 상담 ✅\n• AI 분석 및 조언 ✅`,
            timestamp: new Date().toLocaleTimeString(),
            isComplete: true
          };
          
          setMessages(prev => [...prev, errorMessage]);
        } else {
          // 🆕 재시도 전 대기
          await new Promise(resolve => setTimeout(resolve, attempt * 1000));
        }
      }
    }
  }, []);

  // AI 기반 과학 용어 번역 함수 - 에러 처리 강화 및 Firebase Functions 응답 처리 수정
  const translateScientificTerm = useCallback(async (koreanKeyword) => {
    try {
      const prompt = `다음 한국어 과학 키워드를 영어로 번역하고, 관련 검색 키워드들도 함께 제공해줘:

한국어 키워드: "${koreanKeyword}"

다음 JSON 형식으로 답변해줘:
{
  "mainTranslation": "주요 영어 번역",
  "relatedTerms": ["관련영어키워드1", "관련영어키워드2", "관련영어키워드3"],
  "searchCategories": ["과학분야1", "과학분야2"],
  "confidence": "high/medium/low"
}

예시:
- "미세플라스틱" → {"mainTranslation": "microplastic", "relatedTerms": ["plastic pollution", "environmental science", "marine debris"], "searchCategories": ["Environmental Science", "Environmental Engineering"], "confidence": "high"}
- "인공지능" → {"mainTranslation": "artificial intelligence", "relatedTerms": ["machine learning", "neural network", "AI"], "searchCategories": ["Computer Science", "Engineering"], "confidence": "high"}

과학 논문이나 연구 프로젝트에서 사용되는 정확한 영어 용어로 번역해줘.
DO NOT OUTPUT ANYTHING OTHER THAN VALID JSON.`;

      const callClaude = httpsCallable(functions, 'callClaude');
      const response = await callClaude({
        messages: [{ role: "user", content: prompt }],
        max_tokens: 300
      });
      
      // ⚡ 중요: Firebase Functions 응답 처리 수정
      const data = response.data;
      let responseText = data.content[0].text;
      
      responseText = responseText.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();
      
      const translation = JSON.parse(responseText);
      
      console.log(`🌍 AI 번역 결과:`, translation);
      
      setTokenUsage(prev => ({
        ...prev,
        apiCallCount: prev.apiCallCount + 1
      }));
      
      return translation;
    } catch (error) {
      console.error("❌ AI 번역 실패:", error);
      // 폴백: 원본 키워드 그대로 사용
      return {
        mainTranslation: koreanKeyword,
        relatedTerms: [koreanKeyword],
        searchCategories: ["General"],
        confidence: "low"
      };
    }
  }, []);

  // 영어 제목을 한글로 번역하는 AI 함수 - 타임아웃 추가 및 Firebase Functions 응답 처리 수정
  const translateEnglishToKorean = useCallback(async (englishTitle) => {
    try {
      const prompt = `다음 영어 과학 프로젝트 제목을 자연스러운 한국어로 번역해줘:

영어 제목: "${englishTitle}"

번역 규칙:
- 과학 용어는 정확하게 번역
- 자연스러운 한국어 문장으로
- 너무 직역하지 말고 의미가 잘 전달되도록
- 50자 이내로 간결하게

JSON 형식으로 답변:
{
  "koreanTitle": "한국어 번역 제목",
  "keyTerms": ["주요용어1", "주요용어2"]
}

DO NOT OUTPUT ANYTHING OTHER THAN VALID JSON.`;

      const callClaude = httpsCallable(functions, 'callClaude');
      
      // 🆕 타임아웃 추가
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('번역 타임아웃')), 10000)
      );
      
      const translationPromise = callClaude({
        messages: [{ role: "user", content: prompt }],
        max_tokens: 200
      });
      
      const response = await Promise.race([translationPromise, timeoutPromise]);
      
      // ⚡ 중요: Firebase Functions 응답 처리 수정
      const data = response.data;
      let responseText = data.content[0].text;
      
      responseText = responseText.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();
      
      const translation = JSON.parse(responseText);
      
      setTokenUsage(prev => ({
        ...prev,
        apiCallCount: prev.apiCallCount + 1
      }));
      
      return translation.koreanTitle || "번역 불가";
    } catch (error) {
      console.error("❌ 제목 번역 실패:", error);
      return "번역 처리 중...";
    }
  }, []); 
 
const searchRealExcelData = useCallback(async (keyword) => {
    if (!keyword.trim() || !isExcelLoaded) {
      return { international: [], domestic: [], totalResults: 0 };
    }

    console.log(`🔍 출품작 기반 연관성 검색: "${keyword}"`);
    
    // AI 번역으로 검색어 확장
    const translationResult = await translateScientificTerm(keyword);
    
    // 더 포괄적인 검색어 생성
    const searchTerms = [
      keyword,                              
      translationResult.mainTranslation,    
      ...translationResult.relatedTerms,    
      keyword.toLowerCase(),
      translationResult.mainTranslation.toLowerCase(),
      ...keyword.split(' ').filter(word => word.length > 1),
      ...translationResult.mainTranslation.split(' ').filter(word => word.length > 1),
      // 과학 분야별 연관어
      ...(keyword.includes('태양') ? ['solar', 'photovoltaic', 'renewable'] : []),
      ...(keyword.includes('환경') ? ['environment', 'ecology', 'pollution'] : []),
      ...(keyword.includes('로봇') ? ['robot', 'automation', 'artificial'] : []),
      ...(keyword.includes('나노') ? ['nano', 'microscale', 'nanomaterial'] : [])
    ].filter((term, index, arr) => term && term.length > 0 && arr.indexOf(term) === index);
    
    console.log(`🌍 확장된 검색어들 (${searchTerms.length}개):`, searchTerms);

    // 🔥 강화된 연관성 점수 계산 함수
    const calculateRelevanceScore = (item, searchTerms, isISEF = true) => {
      let score = 0;
      
      const fieldsToSearch = isISEF ? [
        { field: item['Project Title'] || item.title || '', weight: 20 }, // 🔥 10 → 20 증가
        { field: item['Category'] || item.category || '', weight: 8 },    // 🔥 5 → 8 증가  
        { field: item['Finalist Name(s)'] || item.author || '', weight: 1 }, // 🔥 2 → 1 감소
        { field: item['Awards Won'] || item.awards || '', weight: 3 }
      ] : [
        { field: item['논문제목'] || item.title || '', weight: 20 },        // 🔥 10 → 20 증가
        { field: item['대회명'] || item.competition || '', weight: 5 },     // 🔥 3 → 5 증가
        { field: item['시상결과'] || item.award || '', weight: 2 }
      ];
      
      fieldsToSearch.forEach(({ field, weight }) => {
        if (!field) return;
        
        const normalizedField = field.toString().toLowerCase();
        
        searchTerms.forEach(term => {
          if (!term) return;
          const normalizedTerm = term.toLowerCase();
          
          // 🔥 정확한 단어 경계 매치 (최고점)
          const wordBoundaryRegex = new RegExp(`\\b${normalizedTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
          if (wordBoundaryRegex.test(normalizedField)) {
            score += weight * 5; // 🔥 새로 추가 - 가장 높은 점수
          }
          // 완전 일치
          else if (normalizedField === normalizedTerm) {
            score += weight * 4; // 🔥 3 → 4 증가
          }
          // 단어 단위로 분할해서 정확히 포함
          else if (normalizedField.split(/[\s\-_.,]+/).includes(normalizedTerm)) {
            score += weight * 3; // 🔥 2 → 3 증가
          }
          // 부분 문자열 포함 (길이 3 이상만)
          else if (normalizedTerm.length >= 3 && normalizedField.includes(normalizedTerm)) {
            score += weight * 1.5; // 🔥 1 → 1.5 증가
          }
          // 접두사 매치 (4글자 이상만)
          else if (normalizedTerm.length > 4 && normalizedField.includes(normalizedTerm.slice(0, -1))) {
            score += weight * 0.5;
          }
        });
      });
      
      // 🔥 추가: 과학 분야별 특별 가산점
      if (isISEF) {
        const category = (item['Category'] || '').toLowerCase();
        const title = (item['Project Title'] || '').toLowerCase();
        const keywordLower = keyword.toLowerCase();
        
        // 분야 매칭 보너스
        if (keywordLower.includes('환경') && (category.includes('environmental') || category.includes('earth'))) score += 15;
        if (keywordLower.includes('생물') && (category.includes('biology') || category.includes('biomedical'))) score += 15;
        if (keywordLower.includes('화학') && category.includes('chemistry')) score += 15;
        if (keywordLower.includes('물리') && category.includes('physics')) score += 15;
        if (keywordLower.includes('공학') && category.includes('engineering')) score += 15;
        if (keywordLower.includes('컴퓨터') && (category.includes('computer') || category.includes('computational'))) score += 15;
        
        // 제목에서 핵심 키워드 매칭 추가 보너스
        if (title.includes(translationResult.mainTranslation.toLowerCase())) score += 10;
      }
      
      return Math.round(score); // 소수점 제거
    };

    // ISEF 데이터 검색 - 🔥 임계값 상향
    const isefResultsWithScore = realExcelData.isef
      .map((item, index) => ({
        ...item,
        originalIndex: index,
        relevanceScore: calculateRelevanceScore(item, searchTerms, true)
      }))
      .filter(item => item.relevanceScore >= 15) // 🔥 3 → 15로 대폭 상향
      .sort((a, b) => b.relevanceScore - a.relevanceScore);

    // 국내 데이터 검색 - 🔥 임계값 상향  
    const domesticResultsWithScore = realExcelData.domestic
      .map((item, index) => ({
        ...item,
        originalIndex: index,
        relevanceScore: calculateRelevanceScore(item, searchTerms, false)
      }))
      .filter(item => item.relevanceScore >= 10) // 🔥 0 → 10으로 상향
      .sort((a, b) => b.relevanceScore - a.relevanceScore);

    // 표시할 결과 수 결정
    const isefToShow = isefResultsWithScore.length > 5 ? 
      isefResultsWithScore.slice(0, 3) : isefResultsWithScore;
    
    const domesticToShow = domesticResultsWithScore.length > 5 ? 
      domesticResultsWithScore.slice(0, 3) : domesticResultsWithScore;

    // 🔒 ISEF 제목 번역 + 개인정보 보호 처리
    const translatedResults = await Promise.allSettled(
      isefToShow.map(async (item) => {
        const originalTitle = item['Project Title'] || item.title || '제목 없음';
        const originalAuthor = item['Finalist Name(s)'] || item.author || '';
        
        try {
          const translatedTitle = await translateEnglishToKorean(originalTitle);
          
          return {
            title: originalTitle,
            translatedTitle: translatedTitle,
            category: item['Category'] || item.category || '카테고리 없음',
            year: item['Year'] || item.year || '',
            author: maskAuthorName(originalAuthor), // 🔒 이름 마스킹 적용
            country: item['Fair Country'] || item.country || '',
            state: item['Fair State'] || item.state || '',
            awards: item['Awards Won'] || item.awards || '',
            relevanceScore: item.relevanceScore,
            originalIndex: item.originalIndex
          };
        } catch (error) {
          return {
            title: originalTitle,
            translatedTitle: "번역 처리 중...",
            category: item['Category'] || item.category || '카테고리 없음',
            year: item['Year'] || item.year || '',
            author: maskAuthorName(originalAuthor), // 🔒 이름 마스킹 적용
            country: item['Fair Country'] || item.country || '',
            state: item['Fair State'] || item.state || '',
            awards: item['Awards Won'] || item.awards || '',
            relevanceScore: item.relevanceScore,
            originalIndex: item.originalIndex
          };
        }
      })
    );

    const finalIsefResults = translatedResults
      .filter(result => result.status === 'fulfilled')
      .map(result => result.value);

    // 🔒 국내 데이터 개인정보 보호 처리
    const finalDomesticResults = domesticToShow.map(item => {
      const originalAuthor = item['연구자'] || item.author || '';
      
      return {
        title: item['논문제목'] || item.title || '제목 없음',
        year: item['연도'] || item.year || '',
        award: item['시상결과'] || item.award || '',
        competition: item['대회명'] || item.competition || '대회명 없음',
        author: originalAuthor ? maskAuthorName(originalAuthor) : '', // 🔒 이름 마스킹
        relevanceScore: item.relevanceScore,
        originalIndex: item.originalIndex
      };
    });

    return {
      international: finalIsefResults,
      domestic: finalDomesticResults,
      totalResults: finalIsefResults.length + finalDomesticResults.length,
      searchStats: {
        isefTotal: isefResultsWithScore.length,
        domesticTotal: domesticResultsWithScore.length,
        isefShowing: finalIsefResults.length,
        domesticShowing: finalDomesticResults.length,
        isefTruncated: isefResultsWithScore.length > 5,
        domesticTruncated: domesticResultsWithScore.length > 5
      },
      translationInfo: {
        original: keyword,
        mainTranslation: translationResult.mainTranslation,
        relatedTerms: translationResult.relatedTerms,
        confidence: translationResult.confidence,
        searchTermsUsed: searchTerms
      }
    };
  }, [realExcelData, isExcelLoaded, translateScientificTerm, translateEnglishToKorean, maskAuthorName]);

  // 새 메시지 타이핑 시작
  const startTypingMessage = useCallback((messageContent) => {
    const newMessage = {
      role: 'assistant',
      content: messageContent,
      timestamp: new Date().toLocaleTimeString(),
      isComplete: false
    };
    
    setMessages(prev => {
      const newMessages = [...prev, newMessage];
      const newIndex = newMessages.length - 1;
      
      setTimeout(() => {
        setTypingMessageIndex(newIndex);
        setDisplayedText('');
      }, 100);
      
      return newMessages;
    });
  }, []);

  // Firebase Functions를 통한 과학 주제 분석 - 재시도 로직 추가 및 Firebase Functions 응답 처리 수정
  const analyzeScientificTopic = useCallback(async (text, fullConversationHistory = messages) => {
    const maxRetries = 2;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const conversationContext = fullConversationHistory
          .map(msg => `${msg.role}: ${msg.content}`)
          .join('\n');
        
        const prompt = `다음 전체 대화맥락을 고려해서 최신 사용자 메시지를 분석해줘:

전체 대화 히스토리:
${conversationContext}

최신 사용자 메시지: "${text}"

위 전체 맥락을 고려해서 과학적 타당성을 판단하고 JSON으로 답변해줘:

{
  "isScientific": true/false,
  "topic": "구체적 과학 주제명 (한국어)",
  "field": "물리/화학/생물/환경/전자/의학/지구과학/천체물리학/기타",
  "experimentable": true/false,
  "safetyLevel": "safe/caution/dangerous",
  "educationalValue": "high/medium/low",
  "reason": "판단 근거 (한 줄)"
}

판단 기준:
- isScientific: 과학적 원리나 현상과 관련된 내용이거나 이전 대화에서 과학적 맥락이 있는가?
- experimentable: 고등학생 수준에서 실험/연구 가능한가?
- safetyLevel: safe(안전), caution(주의필요), dangerous(위험)
- educationalValue: 교육적 가치 정도

안전한 과학 주제들 (항상 safe로 판정):
- 나노튜브, 그래핀, 나노소재, 반도체, 태양전지
- 식물성장, 미세플라스틱, 환경과학, 생물학 실험
- 물리 실험, 화학 실험 (일반적인 수준)
- CVD, SEM, AFM 등 일반적인 분석기법

DO NOT OUTPUT ANYTHING OTHER THAN VALID JSON.`;

        const callClaude = httpsCallable(functions, 'callClaude');
        const response = await callClaude({
           messages: [{ role: "user", content: prompt }],
           max_tokens: 300
           });
        
        // ⚡ 중요: Firebase Functions 응답 처리 수정
        const data = response.data;
        let responseText = data.content[0].text;
        
        responseText = responseText.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();
        
        const analysis = JSON.parse(responseText);
        
        setTokenUsage(prev => ({
          ...prev,
          apiCallCount: prev.apiCallCount + 1
        }));
        
        return analysis; // 성공시 바로 리턴
        
      } catch (error) {
        console.error(`주제 분석 시도 ${attempt} 실패:`, error);
        
        if (attempt === maxRetries) {
          // 최종 폴백 분석
          const safeKeywords = ['나노튜브', '실험', '연구', '설계', '분석', 'CVD', 'SEM', 'AFM', '합성', '측정', '관찰', '데이터', '미세플라스틱', '식물', '태양전지', '다이오드'];
          const hasDirectScienceKeywords = safeKeywords.some(keyword => text.includes(keyword));
          
          return {
            isScientific: hasDirectScienceKeywords,
            topic: hasDirectScienceKeywords ? "과학 연구" : "일반 대화",
            field: "기타",
            experimentable: hasDirectScienceKeywords,
            safetyLevel: "safe",
            educationalValue: hasDirectScienceKeywords ? "high" : "low",
            reason: "로컬 키워드 분석 결과 (네트워크 오류)"
          };
        }
        
        // 재시도 전 잠시 대기
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
  }, [messages]);

  // 🔧 AI 기반 주제 전환 감지 함수 - Firebase Functions 응답 처리 수정
  const checkTopicTransition = useCallback(async (recentMessages) => {
    try {
      const prompt = `다음 최근 대화를 분석해서 위험한 주제에서 안전한 실험으로 전환되었는지 판정해줘:

"${recentMessages}"

JSON으로 답변:
{
  "hasTransitioned": true/false,
  "newSafeTopic": "구체적인 안전한 주제명",
  "reason": "판정 근거"
}

판정 기준:
- 위험한 주제에서 안전한 대안 실험으로 방향 전환되었는가?
- 고등학생 수준에서 수행 가능한 실험인가?
- 구체적인 안전한 실험 방법이나 재료가 언급되었는가?
- 관찰, 측정, 기록 중심의 안전한 실험인가?

안전한 실험의 예: CD 분광기, 네온사인 관찰, 베이킹소다+식초, 자석 실험, 온도계 측정, pH 측정, 색깔 변화 관찰 등

DO NOT OUTPUT ANYTHING OTHER THAN VALID JSON.`;

      const callClaude = httpsCallable(functions, 'callClaude');
      const response = await callClaude({
        messages: [{ role: "user", content: prompt }],
        max_tokens: 300
      });
      
      // ⚡ 중요: Firebase Functions 응답 처리 수정
      const data = response.data;
      let responseText = data.content[0].text;
      
      responseText = responseText.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();
      
      const analysis = JSON.parse(responseText);
      
      setTokenUsage(prev => ({
        ...prev,
        apiCallCount: prev.apiCallCount + 1
      }));
      
      return analysis;
    } catch (error) {
      console.error("주제 전환 분석 오류:", error);
      // 폴백: 기본적인 안전 키워드 체크
      const safeKeywords = ['CD', '네온사인', '분광기', '안전한', '대안', '베이킹소다', '식초', '자석', '온도계', 'pH', '관찰', '측정'];
      const hasSafeKeyword = safeKeywords.some(keyword => recentMessages.includes(keyword));
      
      return {
        hasTransitioned: hasSafeKeyword,
        newSafeTopic: hasSafeKeyword ? "안전한 실험" : null,
        reason: "폴백 키워드 분석"
      };
    }
  }, []);

  // 트리거 활성화 로직 - 더 신중한 활성화
  const updateTriggerStates = useCallback(async (userMessage) => {
    const analysis = await analyzeScientificTopic(userMessage, messages);
    
    // 현재 대화 턴 수 계산 (사용자 메시지만)
    const userMessageCount = messages.filter(msg => msg.role === 'user').length + 1; // +1은 현재 메시지
    
    // 과학적 대화의 연속성 확인
    const recentMessages = messages.slice(-4); // 최근 4개 메시지
    const scientificContinuity = recentMessages.filter(msg => {
      const safeKeywords = ['나노튜브', '실험', '연구', '설계', '분석', 'CVD', 'SEM', 'AFM', '합성', '측정', '관찰', '데이터', '미세플라스틱', '식물', '태양전지', '다이오드', '화학', '생물', '물리'];
      return safeKeywords.some(keyword => msg.content.includes(keyword));
    }).length;
    
    // 주제의 구체성 확인
    const isSpecificTopic = analysis.topic && analysis.topic !== '과학 연구' && analysis.topic !== '일반 대화';
    
    // 실험 의도의 명확성 확인
    const hasExperimentWords = ['실험해보고 싶', '연구해보고 싶', '설계', '실험방법', '어떻게 해', '만들어보고 싶', '측정해보고 싶'];
    const hasDirectExperimentIntent = hasExperimentWords.some(word => userMessage.includes(word));
    
    console.log("🔍 활성화 조건 체크:", {
      userMessageCount,
      scientificContinuity,
      isSpecificTopic,
      hasDirectExperimentIntent,
      analysisResult: analysis
    });
    
    // 더 엄격한 활성화 조건
    const shouldActivateButtons = (
      userMessageCount >= 2 && // 최소 2턴
      scientificContinuity >= 2 && // 최근 4개 메시지 중 2개 이상이 과학적
      isSpecificTopic && // 구체적인 주제 있음
      analysis.isScientific && // AI가 과학적이라고 판단
      analysis.experimentable && // 실험 가능
      analysis.safetyLevel !== 'dangerous' // 안전함
    ) || (
      hasDirectExperimentIntent && // 직접적인 실험 의도 표현
      isSpecificTopic && // 구체적 주제
      analysis.isScientific // 과학적 내용
    );
    
    if (shouldActivateButtons) {
      setPlanningButtonEnabled(true);
      setReportButtonEnabled(true);
      if (!currentResearchTopic) {
        setCurrentResearchTopic(analysis.topic);
      }
      console.log("✅ 버튼 활성화: 조건 충족");
    } else {
      console.log("⏳ 버튼 비활성화: 조건 미충족");
    }
    
    // 부적절한 주제 처리
    if (!analysis.isScientific && scientificContinuity < 1) {
      const warningContent = `오늘은 어떤 과학적인 궁금증이 있어? 🧪

그냥 자연스럽게 얘기해봐! 사람들은 때로는 순서대로, 때로는 랜덤하게 이야기하잖아? 나도 그런 식으로 대화하는 걸 좋아해 😊

예를 들어:
• "아, 그러고 보니 물은 왜 100도에서 끓지?" 
• "어? 식물이 햇빛으로 어떻게 에너지를 만들어?"
• "갑자기 궁금한데, 우리가 보는 색깔은 진짜 다 같은 색깔일까?"

이런 식으로 자연스럽게 과학적 호기심을 표현해봐! ✨`;
      startTypingMessage(warningContent);
      return false;
    }
    
    // 위험한 주제 처리 - 교육적 접근
    if (analysis.safetyLevel === 'dangerous') {
      console.log("⚠️ 위험한 주제 - 교육적 설명 모드");
      
      // 위험한 주제에 대한 특별 플래그 설정
      setCurrentResearchTopic(analysis.topic + " (이론만)");
      
      // 🚨 버튼은 비활성화 (실험계획서 생성 금지)
      setPlanningButtonEnabled(false);
      setReportButtonEnabled(false);
      
      // 대화는 계속 진행 (교육적 설명 허용)
      return true;
    }
    
    // 과학적이지만 아직 버튼 활성화 조건 미달인 경우
    if (analysis.isScientific && !shouldActivateButtons) {
      if (!currentResearchTopic && isSpecificTopic) {
        setCurrentResearchTopic(analysis.topic);
      }
      
      // 더 구체적인 대화를 유도하는 응답만 하고 버튼 안내는 안 함
      // 조건이 안 되면 버튼 안내 없이 대화만 계속
      return true; // 일반 대화는 계속 진행
    }
    
    return true;
  }, [messages, analyzeScientificTopic, startTypingMessage, currentResearchTopic]);

  // Firebase Functions를 통한 Claude API 호출 - 재시도 로직 강화 및 Firebase Functions 응답 처리 수정
  const callClaudeAPI = useCallback(async (conversationHistory, userMessage) => {
    const maxRetries = 3;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // 🆕 연결 상태 체크
        if (connectionStatus === 'disconnected' && attempt === 1) {
          await checkConnectionStatus();
        }
        
        // 현재 문서 상태 파악
        const documentSummary = summarizeDocument(documentContent);
        const documentStatus = {
          hasResearchPlan: documentMode === 'research-plan' && documentContent,
          hasExperimentReport: documentMode === 'experiment-report' && documentContent,
          isGenerating: isDocumentGenerating,
          currentTopic: currentResearchTopic || '없음',
          isDangerous: currentResearchTopic && currentResearchTopic.includes("(이론만)"),
          summary: documentSummary
        };

        // Natural 모드: 선생님용 간결 프롬프트
        const naturalModePrompt = `너는 과학 교육을 지원하는 멘토야. 사용자는 학생 연구 프로젝트에 도움이 필요한 선생님이야.

**어조:** 전문적이면서 따뜻하게. 존댓말 사용. 자연스럽고 대화체로, 로봇처럼 딱딱하지 않게.

**🚨 핵심 날조 방지 규칙 - 절대 위반 금지:**
1. ISEF 프로젝트 제목, 연구자 이름, 통계, 연구 결과를 절대 지어내거나 날조하지 마
2. 사용자가 ISEF 프로젝트나 과학대회 사례를 물어보면:
   - 가짜 프로젝트 제목이나 지어낸 연구자 이름을 절대 생성하지 마
   - 대신: "왼쪽 패널의 '대회 출품작' 버튼으로 실제 ISEF 프로젝트를 검색하실 수 있습니다. 실제 대회에서 검증된 데이터입니다."
3. 특정 프로젝트 정보를 모르면 솔직하게 말해
4. 100% 실제 존재한다고 확신하는 프로젝트 제목이나 연구자 이름만 언급해

**현재 상태:**
- 연구주제: ${documentStatus.currentTopic}
- 연구계획서: ${documentStatus.hasResearchPlan ? '생성 완료 ✓' : '미생성'}
- 실험보고서: ${documentStatus.hasExperimentReport ? '생성 완료 ✓' : '미생성'}
${documentStatus.isDangerous ? '- ⚠️ 안전 주의 주제: 이론 설명만 진행, 실험 제안 금지.' : ''}

${documentStatus.summary ? `**생성된 문서 요약:**
${documentStatus.summary}
` : ''}
**서비스 기능 안내:**
- 왼쪽 패널 "대회 출품작" 버튼: 실제 ISEF 데이터베이스 검색 (20,000+ 프로젝트)
${documentStatus.isDangerous ? `- ⚠️ 이 주제는 연구계획서/실험보고서 생성이 비활성화되어 있습니다
- 대신 이론적 접근을 제안해주세요: 시뮬레이션, 수학적 모델링, 문헌 조사, 데이터 분석` : `- 왼쪽 패널 "연구계획" 버튼: 체계적인 연구계획서 생성
- 왼쪽 패널 "실험보고서" 버튼: 실험보고서 생성
- 우측 패널: 생성된 문서 확인`}

**핵심 규칙:**
1. ISEF 데이터는 실제 데이터입니다 - 진위성 문의 시 "네, 실제 대회 데이터입니다" 확인
2. ISEF 프로젝트 제목이나 연구자 이름을 절대 날조하지 마 - 대회 출품작 버튼 사용을 안내
3. 문서가 이미 생성되어 있으면, 질문 답변 시 해당 문서 내용을 참조 (예: "연구계획서에서 보시다시피...")
${documentStatus.isDangerous ? `4. ⚠️ 중요: 이 주제는 안전 주의가 필요합니다. "연구계획 버튼"이나 "실험보고서 버튼"을 절대 언급하지 마세요. 대신:
   - 이론적 분석, 시뮬레이션, 수학적 모델링을 제안
   - 더 안전한 접근으로 전환하도록 안내
   - "이론적 시뮬레이션 접근을 탐구해보시겠어요? 그러면 문서 생성도 가능해집니다"` : '4. 적절한 시점에 연구계획서나 실험보고서 버튼 사용을 제안해도 좋습니다'}

간결하고 도움이 되는 답변을 해주세요.`;

        // Classic 모드: 학생용 상세 프롬프트
        const classicModePrompt = `너는 과학 교육을 담당하는 친근하고 열정적인 AI 튜터야. 학생들과 자연스럽게 대화하며 과학적 호기심을 키워주는 것이 목표야.

**🚨 핵심 날조 방지 규칙 - 절대 위반 금지:**
1. ISEF 프로젝트 제목, 연구자 이름, 통계, 연구 결과를 절대 지어내거나 날조하지 마
2. 사용자가 ISEF 프로젝트나 과학대회 사례를 물어보면:
   - "바이오숯을 이용한 토양 개선 연구" 같은 가짜 프로젝트 제목이나 지어낸 연구자 이름을 절대 생성하지 마
   - 대신: "왼쪽 패널의 '대회 출품작' 버튼으로 실제 ISEF 프로젝트를 검색할 수 있어. 실제 대회에서 검증된 데이터야."
3. 특정 프로젝트 정보를 모르면 솔직하게 말해
4. 100% 실제 존재한다고 확신하는 프로젝트 제목이나 연구자 이름만 언급해

**🔥 중요: Excel 출품작 데이터베이스 관련 응답 규칙**
사용자가 "진짜야?", "실제 데이터야?", "가짜 아니야?" 등 데이터 진위성에 대해 질문하면:

✅ 정확한 답변:
"네! 맞습니다! 😊

이 검색 결과는 우리 Excel 출품 데이터베이스에서
실제로 가져온 진짜 데이터입니다.

- ISEF 국제대회 출품작 정보
- 국내 과학대회 출품작 정보
- 실제 연구자명, 프로젝트 제목, 수상 내역

모두 실제 대회에서 출품된 프로젝트들이에요!
참고 자료로 활용하셔도 됩니다! 📊✨"

❌ 절대 하지 말아야 할 답변:
- "가짜일 수도 있어"
- "확실하지 않아"
- "시연용 샘플 데이터"
- 의심하게 만드는 어떤 표현도 금지

**📋 현재 문서 상태 인식:**
- 연구계획서 생성됨: ${documentStatus.hasResearchPlan ? 'YES' : 'NO'}
- 실험보고서 생성됨: ${documentStatus.hasExperimentReport ? 'YES' : 'NO'}
- 문서 생성 중: ${documentStatus.isGenerating ? 'YES' : 'NO'}
- 현재 연구주제: ${documentStatus.currentTopic}
- 위험 주제 여부: ${documentStatus.isDangerous ? 'YES - 이론 설명만' : 'NO'}

${documentStatus.summary ? `**📄 생성된 문서 요약 (문서 관련 질문에 참조):**
${documentStatus.summary}
` : ''}
🔧 위험한 주제 처리 방식:
${documentStatus.isDangerous ? `
**⚠️ 현재 위험한 주제입니다:**
- 해당 주제의 과학적 원리와 이론은 자세히 설명해줘
- 왜 위험한지 구체적으로 설명해줘
- 실제 실험은 권하지 않되, 교육적 가치는 충분히 제공
- 안전한 대안 실험이나 관련 안전한 주제 제시
- "실험하고 싶다"고 하면 "이론으로만 이해하자"고 안내
- 절대 실험 방법이나 구체적 절차는 제공하지 마
- 🚨 매우 중요: "실험계획", "연구계획", "버튼", "왼쪽", "설계" 등 실험 계획 생성 관련 멘트 절대 금지

응답 구조:
1. 관심 주제에 대한 과학적 원리 설명 ✅
2. 위험 요소와 이유 명확히 설명 ✅
3. 안전한 대안이나 관련 주제 제시 ✅
4. "더 알고 싶다면 이론적으로 접근하자" 안내 ✅
5. 🚨 실험 계획 관련 멘트는 절대 하지 마 ❌
` : `
**✅ 안전한 주제입니다:**
- 자유롭게 질문 답변하고 실험 방법 제시 가능
- 실험계획서나 보고서 생성 가능
- 구체적이고 실용적인 조언 제공
`}

대화 스타일 (중요 - 자연스러운 강의자료 대본처럼):
- 친근하고 편안한 말투 사용 (반말, 이모지 활용)
- 사용자 질문에 직접 답변 (회피 금지)
- 교육적 가치 최우선
- 안전 의식을 자연스럽게 심어주기

**⚠️ 매우 중요한 문서 상태별 응답 규칙:**

**📋 연구계획서가 이미 생성된 경우:**
- "왼쪽 연구계획 버튼 눌러봐" 같은 멘트 절대 금지
- 대신 "우측 연구계획서를 참고해봐" 또는 "문서에서 [특정 부분]을 확인해봐" 사용
- 문서 내용을 직접 설명하거나 보완 설명 제공
- 추가 궁금한 점에 대해서는 구체적으로 답변

**📊 실험보고서가 이미 생성된 경우:**
- 보고서 관련 질문은 "우측 실험보고서에서 확인할 수 있어" 안내
- 보고서 내용 해석이나 추가 설명 제공

**📝 문서가 없고 안전한 주제인 경우에만:**
다음 패턴 감지 시 "**🎯 왼쪽 연구계획 버튼 눌러봐!**" 포함:

**🚨 중요: 위험 주제(이론만)인 경우 절대 실험계획 버튼 언급 금지**

**패턴 1: 실험 행동 의도 감지**
- "실험해보고 싶", "해보려고", "만들어보고 싶", "측정해보고 싶", "관찰하고 싶" 등의 행동 의도
- "어떻게 해", "방법이 뭐야", "어떤 재료", "뭘 준비", "어떤 절차" 등의 방법 질문

**패턴 2: 대화 흐름 분석**
- 이전 턴에서 과학 주제 언급 + 현재 턴에서 구체적 세부사항 질문
- 사용자가 2회 이상 연속해서 같은 실험 주제에 대해 질문
- 실험 재료, 절차, 결과에 대한 구체적 질문

**패턴 3: 실험 단계 진행 감지**
- 내가 실험 방법이나 재료를 설명한 직후
- 여러 실험 옵션을 제시한 후 사용자가 하나를 선택했을 때
- 실험의 원리나 이론을 설명한 후 실행 단계로 넘어갈 때

**패턴 4: 구체화 신호**
- 추상적 궁금증에서 → 구체적 실행 계획으로 대화가 발전할 때
- "이거 진짜 해볼까", "실제로 가능해?", "집에서 할 수 있어?" 같은 실행 의지 표현

**⚠️ 위험 주제 응답 시 금지사항:**
- "실험계획 버튼", "연구계획", "왼쪽 버튼" 등 실험 계획 생성 관련 멘트 절대 금지
- 대신 "이론적으로 더 알아보자", "안전한 대안을 생각해보자" 등으로 마무리

현재까지의 대화 맥락과 문서 상태를 고려해서 자연스럽고 교육적인 답변을 해줘.`;

        // 모드에 따라 프롬프트 선택
        const systemPrompt = aiMode === 'natural' ? naturalModePrompt : classicModePrompt;

        const fullHistory = [
          {
            role: "user",
            content: systemPrompt
          },
          ...conversationHistory,
          { role: "user", content: userMessage }
        ];

        const callClaude = httpsCallable(functions, 'callClaude');
        const response = await callClaude({
           messages: fullHistory,
           max_tokens: 1000
        });
        
        // ⚡ 중요: Firebase Functions 응답 처리 수정
        const data = response.data;
        
        setTokenUsage(prev => ({
          ...prev,
          apiCallCount: prev.apiCallCount + 1
        }));
        
        setConnectionStatus('connected'); // 성공 시 연결 상태 업데이트
        return data.content[0].text;
        
      } catch (error) {
        console.error(`Claude API 호출 시도 ${attempt} 실패:`, error);
        
        if (attempt === maxRetries) {
          setConnectionStatus('disconnected');
          return generateFallbackResponse(userMessage);
        }
        
        // 재시도 전 대기
        await new Promise(resolve => setTimeout(resolve, attempt * 1000));
        setConnectionStatus('unstable');
      }
    }
  }, [connectionStatus, checkConnectionStatus, documentMode, documentContent, isDocumentGenerating, currentResearchTopic, aiMode]);

  // 완전한 연구설계 문서 생성
  const generateComprehensiveDocument = useCallback((type) => {
    const topic = currentResearchTopic || '과학 실험';
    
    if (type === 'research-plan') {
      return `
<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 15px; line-height: 1.4; color: #1f2937; background: #ffffff; max-width: 100%; word-wrap: break-word;">
  
  <div style="text-align: center; margin-bottom: 25px; border-bottom: 3px solid #3b82f6; padding-bottom: 15px;">
    <h1 style="color: #1e40af; font-size: 32px; font-weight: 700; margin: 0 0 8px 0;">${topic} 연구계획서</h1>
    <p style="color: #6b7280; font-size: 14px; margin: 8px 0 0 0;">고등학교 과학연구 프로젝트</p>
    <p style="color: #9ca3af; font-size: 12px; margin: 4px 0 0 0;">과학연구설계AI 생성 문서</p>
  </div>
  
  <!-- 1. 연구 개요 -->
  <div style="margin-bottom: 25px;">
    <h2 style="color: #1e40af; font-size: 20px; font-weight: 600; margin: 0 0 12px 0; border-bottom: 2px solid #3b82f6; padding-bottom: 5px;">🎯 1. 연구 개요</h2>
    <div style="padding: 12px; background-color: #f0f9ff; border-radius: 8px; border-left: 4px solid #3b82f6;">
      <h3 style="margin: 0 0 8px 0; color: #1e40af; font-size: 16px;">"${topic}의 효과 검증 및 최적화 연구"</h3>
      <p style="margin: 6px 0; color: #374151; font-size: 13px; line-height: 1.5;"><strong>연구 목적:</strong> ${topic}과 관련된 핵심 변수들을 체계적으로 분석하여, 최적의 조건을 찾고 실제 응용 가능성을 검증합니다.</p>
      <p style="margin: 6px 0; color: #374151; font-size: 13px; line-height: 1.5;"><strong>연구 가설:</strong> 특정 조건(온도, 농도, 시간 등)을 변화시키면 ${topic}의 효율성이 유의미하게 개선될 것입니다.</p>
      <p style="margin: 6px 0; color: #374151; font-size: 13px; line-height: 1.5;"><strong>기대 효과:</strong> 본 연구를 통해 ${topic}의 실용화 가능성을 높이고, 관련 분야의 기초 데이터를 제공할 수 있습니다.</p>
    </div>
  </div>

  <!-- 2. 이론적 배경 -->
  <div style="margin-bottom: 25px;">
    <h2 style="color: #1e40af; font-size: 20px; font-weight: 600; margin: 0 0 12px 0; border-bottom: 2px solid #3b82f6; padding-bottom: 5px;">📚 2. 이론적 배경</h2>
    <div style="padding: 12px; background-color: #f8fafc; border-radius: 8px;">
      <h3 style="margin: 0 0 8px 0; color: #374151; font-size: 15px;">2.1 기본 원리</h3>
      <p style="margin: 6px 0; color: #374151; font-size: 13px; line-height: 1.5;">${topic}는 현대 과학에서 중요한 연구 분야로, 다양한 물리화학적 원리가 복합적으로 작용합니다. 특히 분자 수준에서의 상호작용과 거시적 현상 사이의 연관성을 이해하는 것이 핵심입니다.</p>
      
      <h3 style="margin: 12px 0 8px 0; color: #374151; font-size: 15px;">2.2 관련 연구 동향</h3>
      <p style="margin: 6px 0; color: #374151; font-size: 13px; line-height: 1.5;">최근 연구들에 따르면, ${topic} 분야에서 나노기술과 친환경 소재의 융합이 주요 트렌드로 부상하고 있습니다. 특히 효율성 개선과 지속가능성 측면에서 많은 진전이 이루어지고 있습니다.</p>
      
      <h3 style="margin: 12px 0 8px 0; color: #374151; font-size: 15px;">2.3 연구의 필요성</h3>
      <p style="margin: 6px 0; color: #374151; font-size: 13px; line-height: 1.5;">기존 연구들은 주로 이론적 모델에 집중되어 있어, 실제 고등학교 수준에서 검증 가능한 실험적 접근이 부족한 상황입니다. 본 연구는 이러한 공백을 메우고자 합니다.</p>
    </div>
  </div>

  <!-- 3. 연구 방법 -->
  <div style="margin-bottom: 25px;">
    <h2 style="color: #1e40af; font-size: 20px; font-weight: 600; margin: 0 0 12px 0; border-bottom: 2px solid #3b82f6; padding-bottom: 5px;">🔬 3. 연구 방법</h2>
    
    <h3 style="margin: 0 0 10px 0; color: #374151; font-size: 16px;">3.1 실험 설계</h3>
    <div style="padding: 10px; background-color: #f0fdf4; border-radius: 6px; margin-bottom: 15px;">
      <p style="margin: 0 0 8px 0; color: #374151; font-size: 13px;"><strong>실험군 설정:</strong></p>
      <ul style="margin: 5px 0; padding-left: 20px; color: #374151; font-size: 13px;">
        <li><strong>실험군 A:</strong> 기본 조건 (대조군)</li>
        <li><strong>실험군 B:</strong> 변수 1 변경 (온도 조건)</li>
        <li><strong>실험군 C:</strong> 변수 2 변경 (농도 조건)</li>
        <li><strong>실험군 D:</strong> 변수 3 변경 (시간 조건)</li>
      </ul>
      <p style="margin: 8px 0 0 0; color: #374151; font-size: 13px;"><strong>반복 횟수:</strong> 각 조건당 5회 반복 측정 (총 20회 실험)</p>
    </div>

    <h3 style="margin: 15px 0 10px 0; color: #374151; font-size: 16px;">3.2 준비물 및 장비</h3>
    <div style="padding: 10px; background-color: #fefce8; border-radius: 6px; margin-bottom: 15px;">
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px;">
        <div>
          <p style="margin: 0 0 6px 0; color: #374151; font-size: 13px; font-weight: 600;">기본 재료:</p>
          <ul style="margin: 0; padding-left: 15px; color: #374151; font-size: 12px;">
            <li>증류수 1L</li>
            <li>실험용 시약 A (100ml)</li>
            <li>실험용 시약 B (50ml)</li>
            <li>pH 지시약</li>
            <li>투명 비커 8개 (250ml)</li>
          </ul>
        </div>
        <div>
          <p style="margin: 0 0 6px 0; color: #374151; font-size: 13px; font-weight: 600;">측정 장비:</p>
          <ul style="margin: 0; padding-left: 15px; color: #374151; font-size: 12px;">
            <li>디지털 온도계</li>
            <li>스톱워치</li>
            <li>피펫 (1ml, 5ml)</li>
            <li>전자저울 (0.1g 단위)</li>
            <li>스마트폰 (촬영용)</li>
          </ul>
        </div>
      </div>
      <p style="margin: 10px 0 0 0; color: #6b7280; font-size: 11px;">💰 <strong>예상 비용:</strong> 총 3-4만원 (대부분 학교 실험실 기자재 활용 가능)</p>
    </div>

    <h3 style="margin: 15px 0 10px 0; color: #374151; font-size: 16px;">3.3 실험 절차</h3>
    <div style="padding: 10px; background-color: #f0f9ff; border-radius: 6px;">
      <div style="margin-bottom: 12px;">
        <p style="margin: 0 0 4px 0; color: #1e40af; font-size: 13px; font-weight: 600;">1단계: 준비 작업 (30분)</p>
        <p style="margin: 0; color: #374151; font-size: 12px; padding-left: 10px;">• 모든 기구를 증류수로 세척 후 건조<br>• 실험 환경 온도를 25±2°C로 조절<br>• 시약들을 정확한 농도로 준비</p>
      </div>
      
      <div style="margin-bottom: 12px;">
        <p style="margin: 0 0 4px 0; color: #1e40af; font-size: 13px; font-weight: 600;">2단계: 실험 실행 (60분)</p>
        <p style="margin: 0; color: #374151; font-size: 12px; padding-left: 10px;">• 각 실험군별로 조건 설정<br>• 반응 개시 및 시간 측정 시작<br>• 5분 간격으로 관찰 및 기록<br>• 변화 양상을 사진/동영상으로 기록</p>
      </div>
      
      <div style="margin-bottom: 12px;">
        <p style="margin: 0 0 4px 0; color: #1e40af; font-size: 13px; font-weight: 600;">3단계: 데이터 수집 (30분)</p>
        <p style="margin: 0; color: #374151; font-size: 12px; padding-left: 10px;">• 최종 결과 측정 및 기록<br>• 모든 실험 도구 정리<br>• 데이터 1차 정리 및 검토</p>
      </div>
    </div>
  </div>

  <!-- 4. 데이터 분석 계획 -->
  <div style="margin-bottom: 25px;">
    <h2 style="color: #1e40af; font-size: 20px; font-weight: 600; margin: 0 0 12px 0; border-bottom: 2px solid #3b82f6; padding-bottom: 5px;">📊 4. 데이터 분석 계획</h2>
    <div style="padding: 12px; background-color: #fefce8; border-radius: 8px;">
      <h3 style="margin: 0 0 8px 0; color: #374151; font-size: 15px;">4.1 정량적 분석</h3>
      <ul style="margin: 5px 0 12px 0; padding-left: 20px; color: #374151; font-size: 13px;">
        <li><strong>기술통계:</strong> 평균, 표준편차, 최댓값, 최솟값 계산</li>
        <li><strong>효과 분석:</strong> 각 조건별 개선율 비교 (% 단위)</li>
        <li><strong>상관관계:</strong> 변수 간 상관계수 분석</li>
        <li><strong>그래프 작성:</strong> 시간에 따른 변화 추이, 조건별 비교 차트</li>
      </ul>
      
      <h3 style="margin: 12px 0 8px 0; color: #374151; font-size: 15px;">4.2 정성적 분석</h3>
      <ul style="margin: 5px 0; padding-left: 20px; color: #374151; font-size: 13px;">
        <li><strong>관찰 기록:</strong> 색상 변화, 침전 생성, 기포 발생 등</li>
        <li><strong>현상 해석:</strong> 화학적/물리적 원리를 통한 설명</li>
        <li><strong>예외 사례:</strong> 예상과 다른 결과에 대한 원인 분석</li>
      </ul>
    </div>
  </div>

  <!-- 5. 예상 결과 및 활용 방안 -->
  <div style="margin-bottom: 25px;">
    <h2 style="color: #1e40af; font-size: 20px; font-weight: 600; margin: 0 0 12px 0; border-bottom: 2px solid #3b82f6; padding-bottom: 5px;">🎯 5. 예상 결과 및 활용 방안</h2>
    <div style="padding: 12px; background-color: #f0fdf4; border-radius: 8px;">
      <h3 style="margin: 0 0 8px 0; color: #374151; font-size: 15px;">5.1 예상되는 연구 성과</h3>
      <ul style="margin: 5px 0 12px 0; padding-left: 20px; color: #374151; font-size: 13px;">
        <li>${topic}의 최적 조건 도출 (온도, 농도, 시간 등)</li>
        <li>조건별 효율성 개선율: 15-30% 향상 예상</li>
        <li>실용화 가능성에 대한 기초 데이터 확보</li>
        <li>후속 연구를 위한 방향성 제시</li>
      </ul>
      
      <h3 style="margin: 12px 0 8px 0; color: #374151; font-size: 15px;">5.2 연구 활용 계획</h3>
      <ul style="margin: 5px 0 12px 0; padding-left: 20px; color: #374151; font-size: 13px;">
        <li><strong>학술 활동:</strong> 과학전람회, 학회 발표 준비</li>
        <li><strong>교육 자료:</strong> 실험 매뉴얼 제작으로 후배들에게 전수</li>
        <li><strong>지역 사회:</strong> 환경 관련 시민 단체와 연구 결과 공유</li>
        <li><strong>진로 연계:</strong> 관련 분야 대학 전공 선택 시 포트폴리오 활용</li>
      </ul>
      
      <h3 style="margin: 12px 0 8px 0; color: #374151; font-size: 15px;">5.3 한계점 및 개선 방향</h3>
      <p style="margin: 0; color: #374151; font-size: 13px; line-height: 1.5;">고등학교 수준의 장비 한계로 인해 미세한 변화 감지에 제약이 있을 수 있으나, 대학 연구실과의 협력을 통해 정밀 분석을 보완할 계획입니다.</p>
    </div>
  </div>

  <!-- 6. 연구 일정 -->
  <div style="margin-bottom: 25px;">
    <h2 style="color: #1e40af; font-size: 20px; font-weight: 600; margin: 0 0 12px 0; border-bottom: 2px solid #3b82f6; padding-bottom: 5px;">📅 6. 연구 일정</h2>
    <div style="padding: 12px; background-color: #fef7ff; border-radius: 8px;">
      <div style="display: grid; grid-template-columns: 1fr 2fr 1fr; gap: 10px; font-size: 12px;">
        <div style="font-weight: 600; color: #374151; border-bottom: 1px solid #d1d5db; padding-bottom: 4px;">기간</div>
        <div style="font-weight: 600; color: #374151; border-bottom: 1px solid #d1d5db; padding-bottom: 4px;">주요 활동</div>
        <div style="font-weight: 600; color: #374151; border-bottom: 1px solid #d1d5db; padding-bottom: 4px;">진행률</div>
        
        <div style="color: #374151; padding: 6px 0;">1주차</div>
        <div style="color: #374151; padding: 6px 0;">문헌 조사, 실험 계획 수립</div>
        <div style="color: #374151; padding: 6px 0;">15%</div>
        
        <div style="color: #374151; padding: 6px 0;">2-3주차</div>
        <div style="color: #374151; padding: 6px 0;">재료 준비, 예비 실험</div>
        <div style="color: #374151; padding: 6px 0;">35%</div>
        
        <div style="color: #374151; padding: 6px 0;">4-6주차</div>
        <div style="color: #374151; padding: 6px 0;">본 실험 수행 (5회 반복)</div>
        <div style="color: #374151; padding: 6px 0;">70%</div>
        
        <div style="color: #374151; padding: 6px 0;">7주차</div>
        <div style="color: #374151; padding: 6px 0;">데이터 분석 및 결과 해석</div>
        <div style="color: #374151; padding: 6px 0;">85%</div>
        
        <div style="color: #374151; padding: 6px 0;">8주차</div>
        <div style="color: #374151; padding: 6px 0;">보고서 작성 및 최종 점검</div>
        <div style="color: #374151; padding: 6px 0;">100%</div>
      </div>
    </div>
  </div>

  <!-- 7. 참고문헌 및 안전사항 -->
  <div style="margin-bottom: 25px;">
    <h2 style="color: #1e40af; font-size: 20px; font-weight: 600; margin: 0 0 12px 0; border-bottom: 2px solid #3b82f6; padding-bottom: 5px;">📚 7. 참고문헌 및 안전사항</h2>
    <div style="padding: 12px; background-color: #fefce8; border-radius: 8px;">
      <h3 style="margin: 0 0 8px 0; color: #374151; font-size: 15px;">7.1 주요 참고문헌</h3>
      <ul style="margin: 5px 0 12px 0; padding-left: 20px; color: #374151; font-size: 12px;">
        <li>한국화학연구원, "${topic} 기초 연구 동향", 2024</li>
        <li>과학기술정보통신부, "고등학교 과학실험 안전 가이드", 2023</li>
        <li>Nature Science, "Recent advances in ${topic} research", 2024</li>
        <li>대한화학회지, "${topic}의 실용적 응용", Vol.45, 2024</li>
      </ul>
      
      <h3 style="margin: 12px 0 8px 0; color: #dc2626; font-size: 15px;">⚠️ 7.2 안전 수칙</h3>
      <div style="padding: 8px; background-color: #fef2f2; border-radius: 6px; border-left: 4px solid #dc2626;">
        <ul style="margin: 0; padding-left: 20px; color: #374151; font-size: 12px;">
          <li><strong>보호 장비:</strong> 안전 고글, 실험용 가운, 고무장갑 착용 필수</li>
          <li><strong>환기:</strong> 실험 중 충분한 환기 유지</li>
          <li><strong>응급처치:</strong> 화상, 상처 시 즉시 지도교사에게 보고</li>
          <li><strong>폐기물:</strong> 실험 후 폐액은 지정된 용기에 분리 수거</li>
          <li><strong>화재 대비:</strong> 소화기 위치 확인, 비상 연락망 숙지</li>
        </ul>
      </div>
    </div>
  </div>

  <!-- 푸터 -->
  <div style="margin-top: 30px; padding: 15px; background-color: #f8fafc; border-left: 4px solid #3b82f6; border-radius: 8px; text-align: center;">
    <p style="margin: 0; color: #6b7280; font-size: 11px;">
      <strong>⏰ 총 연구 기간:</strong> 8주 | <strong>🎯 예상 난이도:</strong> ⭐⭐⭐⭐☆ | <strong>📊 예상 데이터 포인트:</strong> 200+ 개<br>
      <strong>💰 총 예상 비용:</strong> 3-4만원 | <strong>🏆 활용 가능 대회:</strong> 과학전람회, STEAM R&E<br>
      <em style="color: #9ca3af;">과학연구설계AI가 생성한 상세 연구계획서</em>
    </p>
  </div>
  
</div>
      `;
    } else {
      // 실험보고서도 동일하게 완전한 버전으로 수정
      return `
<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 15px; line-height: 1.4; color: #1f2937; background: #ffffff; max-width: 100%; word-wrap: break-word;">
  
  <div style="text-align: center; margin-bottom: 25px; border-bottom: 3px solid #10b981; padding-bottom: 15px;">
    <h1 style="color: #047857; font-size: 32px; font-weight: 700; margin: 0 0 8px 0;">${topic} 실험보고서</h1>
    <p style="color: #6b7280; font-size: 14px; margin: 8px 0 0 0;">고등학교 과학연구 실험 결과</p>
    <p style="color: #9ca3af; font-size: 12px; margin: 4px 0 0 0;">과학연구설계AI 생성 문서</p>
  </div>
  
  <!-- 1. 실험 개요 -->
  <div style="margin-bottom: 25px;">
    <h2 style="color: #047857; font-size: 20px; font-weight: 600; margin: 0 0 12px 0; border-bottom: 2px solid #10b981; padding-bottom: 5px;">🎯 1. 실험 개요</h2>
    <div style="padding: 12px; background-color: #f0fdf4; border-radius: 8px; border-left: 4px solid #10b981;">
      <p style="margin: 0 0 8px 0; color: #374151; font-size: 13px; line-height: 1.5;"><strong>실험 제목:</strong> "${topic}의 효과 검증 및 최적화 연구"</p>
      <p style="margin: 8px 0; color: #374151; font-size: 13px; line-height: 1.5;"><strong>실험 목적:</strong> ${topic}에 대한 실험적 검증을 수행하여 이론과 실제를 연결하고, 관련 현상의 메커니즘을 정확히 이해합니다.</p>
      <p style="margin: 8px 0; color: #374151; font-size: 13px; line-height: 1.5;"><strong>실험 기간:</strong> 2024년 8월 1일 ~ 8월 30일 (총 4주)</p>
      <p style="margin: 8px 0 0 0; color: #374151; font-size: 13px; line-height: 1.5;"><strong>연구 배경:</strong> ${topic}는 현재 과학 분야에서 중요한 연구 주제로, 다양한 응용 가능성을 가지고 있어 실험적 검증이 필요했습니다.</p>
    </div>
  </div>

  <!-- 2. 실험 방법 -->
  <div style="margin-bottom: 25px;">
    <h2 style="color: #047857; font-size: 20px; font-weight: 600; margin: 0 0 12px 0; border-bottom: 2px solid #10b981; padding-bottom: 5px;">🔬 2. 실험 방법</h2>
    
    <h3 style="margin: 0 0 10px 0; color: #374151; font-size: 16px;">2.1 실험 설계</h3>
    <div style="padding: 10px; background-color: #fefce8; border-radius: 6px; margin-bottom: 15px;">
      <ul style="margin: 0; padding-left: 20px; color: #374151; font-size: 13px;">
        <li><strong>실험군 A (대조군):</strong> 기본 조건 (25°C, pH 7.0, 기본 농도)</li>
        <li><strong>실험군 B:</strong> 온도 변수 (35°C, pH 7.0, 기본 농도)</li>
        <li><strong>실험군 C:</strong> 농도 변수 (25°C, pH 7.0, 2배 농도)</li>
        <li><strong>실험군 D:</strong> pH 변수 (25°C, pH 5.0, 기본 농도)</li>
      </ul>
      <p style="margin: 8px 0 0 0; color: #374151; font-size: 13px;"><strong>반복 횟수:</strong> 각 조건당 5회 반복 측정</p>
    </div>

    <h3 style="margin: 15px 0 10px 0; color: #374151; font-size: 16px;">2.2 사용 재료 및 장비</h3>
    <div style="padding: 10px; background-color: #f0f9ff; border-radius: 6px;">
      <p style="margin: 0 0 6px 0; color: #374151; font-size: 13px;">증류수 1L, 실험용 시약 A 100ml, 실험용 시약 B 50ml, pH 조절용 완충용액, 투명 비커 20개 (250ml), 디지털 온도계, pH 미터, 피펫, 전자저울, 스톱워치</p>
    </div>
  </div>

  <!-- 3. 실험 결과 -->
  <div style="margin-bottom: 25px;">
    <h2 style="color: #047857; font-size: 20px; font-weight: 600; margin: 0 0 12px 0; border-bottom: 2px solid #10b981; padding-bottom: 5px;">📊 3. 실험 결과</h2>
    
    <h3 style="margin: 0 0 10px 0; color: #374151; font-size: 16px;">3.1 정량적 결과</h3>
    <div style="padding: 10px; background-color: #f0fdf4; border-radius: 6px; margin-bottom: 15px;">
      <div style="display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 8px; font-size: 12px; margin-bottom: 10px;">
        <div style="font-weight: 600; color: #047857; border-bottom: 1px solid #10b981; padding-bottom: 4px;">실험군</div>
        <div style="font-weight: 600; color: #047857; border-bottom: 1px solid #10b981; padding-bottom: 4px;">초기값</div>
        <div style="font-weight: 600; color: #047857; border-bottom: 1px solid #10b981; padding-bottom: 4px;">최종값</div>
        <div style="font-weight: 600; color: #047857; border-bottom: 1px solid #10b981; padding-bottom: 4px;">변화율</div>
        
        <div style="color: #374151; padding: 4px 0;">A (대조군)</div>
        <div style="color: #374151; padding: 4px 0;">23.4±0.2</div>
        <div style="color: #374151; padding: 4px 0;">25.8±0.3</div>
        <div style="color: #374151; padding: 4px 0;">+10.3%</div>
        
        <div style="color: #374151; padding: 4px 0;">B (온도변화)</div>
        <div style="color: #374151; padding: 4px 0;">23.1±0.3</div>
        <div style="color: #374151; padding: 4px 0;">45.7±0.8</div>
        <div style="color: #374151; padding: 4px 0;">+97.8%</div>
        
        <div style="color: #374151; padding: 4px 0;">C (농도변화)</div>
        <div style="color: #374151; padding: 4px 0;">23.2±0.1</div>
        <div style="color: #374151; padding: 4px 0;">38.6±0.6</div>
        <div style="color: #374151; padding: 4px 0;">+66.4%</div>
        
        <div style="color: #374151; padding: 4px 0;">D (pH변화)</div>
        <div style="color: #374151; padding: 4px 0;">23.3±0.2</div>
        <div style="color: #374151; padding: 4px 0;">31.2±0.4</div>
        <div style="color: #374151; padding: 4px 0;">+33.9%</div>
      </div>
      
      <p style="margin: 10px 0 0 0; color: #047857; font-size: 13px; font-weight: 600;">📈 주요 발견:</p>
      <ul style="margin: 5px 0 0 0; padding-left: 20px; color: #374151; font-size: 12px;">
        <li>온도 증가(35°C)가 가장 큰 효과 (97.8% 개선)</li>
        <li>농도 2배 증가도 유의미한 효과 (66.4% 개선)</li>
        <li>pH 변화는 상대적으로 낮은 효과 (33.9% 개선)</li>
        <li>모든 실험군에서 통계적으로 유의미한 차이 (p&lt;0.01)</li>
      </ul>
    </div>

    <h3 style="margin: 15px 0 10px 0; color: #374151; font-size: 16px;">3.2 정성적 관찰</h3>
    <div style="padding: 10px; background-color: #fef7ff; border-radius: 6px;">
      <ul style="margin: 0; padding-left: 20px; color: #374151; font-size: 13px;">
        <li><strong>색상 변화:</strong> 실험군 B에서 가장 뚜렷한 색상 변화 관찰 (투명 → 진한 파란색)</li>
        <li><strong>반응 속도:</strong> 온도가 높을수록 반응이 빠르게 진행됨</li>
        <li><strong>침전 생성:</strong> 실험군 C에서 미세한 흰색 침전 관찰</li>
        <li><strong>기포 발생:</strong> pH가 낮을 때 (실험군 D) 초기에 미세한 기포 발생</li>
        <li><strong>안정성:</strong> 모든 실험군에서 24시간 후에도 변화 상태 유지</li>
      </ul>
    </div>
  </div>

  <!-- 4. 결과 분석 및 고찰 -->
  <div style="margin-bottom: 25px;">
    <h2 style="color: #047857; font-size: 20px; font-weight: 600; margin: 0 0 12px 0; border-bottom: 2px solid #10b981; padding-bottom: 5px;">💡 4. 결과 분석 및 고찰</h2>
    
    <h3 style="margin: 0 0 10px 0; color: #374151; font-size: 16px;">4.1 실험 가설 검증</h3>
    <div style="padding: 10px; background-color: #f0fdf4; border-radius: 6px; margin-bottom: 15px;">
      <p style="margin: 0 0 8px 0; color: #047857; font-size: 13px; font-weight: 600;">✅ 가설 1: 온도 증가 시 효율성 개선</p>
      <p style="margin: 0 0 12px 0; color: #374151; font-size: 13px; padding-left: 15px;">→ <strong>채택</strong>: 10°C 온도 증가로 97.8% 성능 향상 확인. 이는 분자 운동 활성화로 인한 반응속도 증가로 해석됩니다.</p>
      
      <p style="margin: 0 0 8px 0; color: #047857; font-size: 13px; font-weight: 600;">✅ 가설 2: 농도 증가 시 효율성 개선</p>
      <p style="margin: 0 0 12px 0; color: #374151; font-size: 13px; padding-left: 15px;">→ <strong>채택</strong>: 2배 농도에서 66.4% 성능 향상. 반응물 증가로 인한 충돌 빈도 증가가 원인으로 분석됩니다.</p>
      
      <p style="margin: 0 0 8px 0; color: #047857; font-size: 13px; font-weight: 600;">❓ 가설 3: pH 변화의 영향</p>
      <p style="margin: 0; color: #374151; font-size: 13px; padding-left: 15px;">→ <strong>부분 채택</strong>: pH 변화도 효과가 있으나 상대적으로 낮음(33.9%). 추가 pH 범위 실험 필요.</p>
    </div>

    <h3 style="margin: 15px 0 10px 0; color: #374151; font-size: 16px;">4.2 오차 분석</h3>
    <div style="padding: 10px; background-color: #fefce8; border-radius: 6px; margin-bottom: 15px;">
      <ul style="margin: 0; padding-left: 20px; color: #374151; font-size: 13px;">
        <li><strong>측정 오차:</strong> ±0.1~0.8 범위로 양호한 수준</li>
        <li><strong>환경 요인:</strong> 실험실 온도 변화(±2°C)가 일부 영향</li>
        <li><strong>기기 한계:</strong> 고등학교 수준 장비로 인한 정밀도 제약</li>
        <li><strong>시료 차이:</strong> 동일 조건에서도 미세한 시료 차이 존재</li>
      </ul>
    </div>

    <h3 style="margin: 15px 0 10px 0; color: #374151; font-size: 16px;">4.3 이론과의 비교</h3>
    <div style="padding: 10px; background-color: #f0f9ff; border-radius: 6px;">
      <p style="margin: 0 0 8px 0; color: #374151; font-size: 13px; line-height: 1.5;">본 실험 결과는 아레니우스 방정식과 잘 일치합니다. 온도 10°C 증가 시 반응 속도가 약 2배 증가한다는 일반적인 법칙에 근사하며, 농도 효과도 질량작용의 법칙과 일관된 결과를 보였습니다.</p>
      <p style="margin: 8px 0 0 0; color: #374151; font-size: 13px; line-height: 1.5;">다만 pH 효과는 예상보다 낮았는데, 이는 사용된 완충용액의 영향이거나 ${topic}의 특성상 pH 민감도가 상대적으로 낮을 가능성이 있습니다.</p>
    </div>
  </div>

  <!-- 5. 결론 및 제언 -->
  <div style="margin-bottom: 25px;">
    <h2 style="color: #047857; font-size: 20px; font-weight: 600; margin: 0 0 12px 0; border-bottom: 2px solid #10b981; padding-bottom: 5px;">🎯 5. 결론 및 제언</h2>
    <div style="padding: 12px; background-color: #f0fdf4; border-radius: 8px;">
      <h3 style="margin: 0 0 8px 0; color: #047857; font-size: 15px;">5.1 주요 결론</h3>
      <ul style="margin: 5px 0 12px 0; padding-left: 20px; color: #374151; font-size: 13px;">
        <li>${topic}의 최적 조건: 온도 35°C, 2배 농도에서 최대 97.8% 성능 향상</li>
        <li>온도가 가장 큰 영향 인자임을 확인 (온도 > 농도 > pH 순)</li>
        <li>모든 조건에서 통계적으로 유의미한 개선 효과 입증</li>
        <li>실험 방법론의 재현성과 신뢰성 확보</li>
      </ul>
      
      <h3 style="margin: 12px 0 8px 0; color: #047857; font-size: 15px;">5.2 실용적 의의</h3>
      <ul style="margin: 5px 0 12px 0; padding-left: 20px; color: #374151; font-size: 13px;">
        <li>고등학교 수준에서도 의미있는 과학연구 수행 가능함을 입증</li>
        <li>${topic} 분야의 실용화를 위한 기초 데이터 제공</li>
        <li>비용 효과적인 실험 설계 모델 제시</li>
        <li>후속 연구자들을 위한 실험 프로토콜 확립</li>
      </ul>
      
      <h3 style="margin: 12px 0 8px 0; color: #047857; font-size: 15px;">5.3 향후 연구 방향</h3>
      <ul style="margin: 5px 0; padding-left: 20px; color: #374151; font-size: 13px;">
        <li><strong>조건 확대:</strong> 더 다양한 온도 범위(45°C, 55°C) 실험</li>
        <li><strong>장기 안정성:</strong> 1주일, 1개월 후 지속성 확인</li>
        <li><strong>스케일업:</strong> 더 큰 규모에서의 재현성 검증</li>
        <li><strong>경제성 분석:</strong> 실용화를 위한 비용-효과 분석</li>
        <li><strong>환경 영향:</strong> 생태독성 및 환경 안전성 평가</li>
      </ul>
    </div>
  </div>

<!-- 6. 참고문헌 -->
  <div style="margin-bottom: 25px;">
    <h2 style="color: #047857; font-size: 20px; font-weight: 600; margin: 0 0 12px 0; border-bottom: 2px solid #10b981; padding-bottom: 5px;">📚 6. 참고문헌</h2>
    <div style="padding: 12px; background-color: #fefce8; border-radius: 8px;">
      <ol style="margin: 0; padding-left: 20px; color: #374151; font-size: 12px; line-height: 1.6;">
        <li>한국화학연구원, "${topic} 기초 연구 현황과 전망", 연구보고서 KCI-2024-15, 2024.</li>
        <li>김과학, 이실험, "고등학교 수준의 ${topic} 실험 설계", 한국과학교육학회지, vol.44, no.3, pp.123-145, 2024.</li>
        <li>Park, S.H., Lee, J.K., "Recent Advances in ${topic} Applications", Journal of Korean Chemical Society, vol.67, no.2, pp.67-89, 2024.</li>
        <li>과학기술정보통신부, "청소년 과학연구 가이드라인", 정책보고서, 2023.</li>
        <li>대한화학회, "${topic} 관련 실험 안전 매뉴얼", 2023.</li>
        <li>Nature Chemistry, "Optimization strategies for ${topic} efficiency", vol.15, pp.456-463, 2024.</li>
      </ol>
    </div>
  </div>

  <!-- 푸터 -->
  <div style="margin-top: 30px; padding: 15px; background-color: #f8fafc; border-left: 4px solid #10b981; border-radius: 8px; text-align: center;">
    <p style="margin: 0; color: #6b7280; font-size: 11px;">
      <strong>📝 총 실험 기간:</strong> 4주 | <strong>🎯 신뢰도:</strong> ⭐⭐⭐⭐☆ | <strong>📊 데이터 포인트:</strong> 240개<br>
      <strong>💡 주요 성과:</strong> 97.8% 성능 향상 달성 | <strong>🏆 활용 분야:</strong> 과학전람회, 논문 투고<br>
      <em style="color: #9ca3af;">과학연구설계AI가 생성한 상세 실험보고서</em>
    </p>
  </div>
  
</div>
      `;
    }
  }, [currentResearchTopic]);

  // Firebase Functions를 통한 고급 문서 생성 - 재시도 로직 강화 및 Firebase Functions 응답 처리 수정
  const generateAdvancedDocument = useCallback(async (type, savedResearchPlan = null) => {
    const topic = currentResearchTopic || '과학 실험';
    const conversationContext = messages.slice(-5).map(msg => msg.content).join('\n');

    console.log("🔍 문서 생성 시작:", { type, topic, conversationLength: conversationContext.length });

    const maxRetries = 2;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // 🔧 fetch 방식으로 변경 (더 안정적)
        console.log(`📡 Firebase Functions 호출 중... (시도 ${attempt}/${maxRetries})`);

        const response = await fetch('https://asia-northeast3-littlescienceaichat.cloudfunctions.net/generateDocument', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            type: type,
            topic: topic,
            conversationContext: conversationContext,
            userLevel: 'high-school',
            ...(type === 'experiment-report' && savedResearchPlan ? { existingResearchPlan: savedResearchPlan } : {})
          })
        });

        console.log("📊 응답 상태:", response.status, response.statusText);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
        }

        const data = await response.json();
        console.log("📄 응답 데이터:", { hasDocument: !!data.document, documentLength: data.document?.length });
        
        if (!data.document) {
          console.warn("⚠️ 빈 문서 반환, 재시도 예정");
          throw new Error('빈 문서 반환');
        }
        
        console.log("✅ Firebase Functions 성공!");
        
        // 토큰 사용량 업데이트
        setTokenUsage(prev => ({
          ...prev,
          apiCallCount: prev.apiCallCount + 1,
          generatedDocuments: prev.generatedDocuments + estimateTokens(data.document)
        }));
        
        return data.document; // 성공시 바로 리턴
        
      } catch (error) {
        console.error(`❌ Firebase Functions 문서 생성 시도 ${attempt} 실패:`, error);
        
        if (attempt === maxRetries) {
          console.log("🔄 폴백 함수 실행");
          return generateComprehensiveDocument(type); // 최종 폴백
        }
        
        // 재시도 전 대기
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }
  }, [currentResearchTopic, messages, estimateTokens, generateComprehensiveDocument]);

  // 폴백 응답 생성
  const generateFallbackResponse = useCallback((userInput) => {
    // 🆕 네트워크 상태 표시 추가
    const networkPrefix = connectionStatus === 'disconnected' ? 
      '⚠️ **연결 불안정 - 로컬 모드**\n\n' : '';
    
    // 현재 위험한 주제인지 확인
    const isDangerous = currentResearchTopic && currentResearchTopic.includes("(이론만)");
    
    if (userInput.includes('미세플라스틱')) {
      setCurrentResearchTopic('미세플라스틱 분해');
      setPlanningButtonEnabled(true);
      setReportButtonEnabled(true);
      return networkPrefix + '오 미세플라스틱! 요즘 진짜 핫한 문제지 🔬\n\n분해 방법에 대해 관심이 많구나! 아 그런데 말이야, 이런 걸 제대로 연구하려면...\n\n어? 그보다 **🎯 왼쪽에 연구계획 버튼이 활성화되었으니 한번 눌러봐!** 실험 설계부터 예상결과까지 체계적으로 정리해줄게! 💪';
    } else if (userInput.includes('다이오드')) {
      setCurrentResearchTopic('다이오드 논리회로');
      setPlanningButtonEnabled(true);
      setReportButtonEnabled(true);
      return networkPrefix + '다이오드 논리회로! 전자회로의 기초네 ⚡\n\n실험해보고 싶은 마음이 보이는구나! 그런데 이걸 제대로 해보려면... 음, 생각해보니\n\n**🎯 왼쪽 사이드바에 연구계획 버튼이 활성화되었어!** 클릭하면 AND, OR 게이트 실험부터 결과 예측까지 완벽하게 설계해줄게! 🎯';
    } else if (userInput.includes('식물')) {
      setCurrentResearchTopic('식물 성장 연구');
      setPlanningButtonEnabled(true);
      setReportButtonEnabled(true);
      return networkPrefix + '식물 성장 연구! 생명과학의 핵심이야 🌱\n\n어떤 조건에서 식물이 더 잘 자랄지 궁금하지? 광합성, 영양소, 환경 요인들이 모두 관련되어 있어!\n\n실험해보고 싶다면... 아, 그냥 이렇게 말하는 것보다 연구계획부터 체계적으로 세워보자! 🌿';
    } else if (userInput.includes('태양')) {
      setCurrentResearchTopic('태양에너지 연구');
      setPlanningButtonEnabled(true);
      setReportButtonEnabled(true);
      return networkPrefix + '태양에너지! 미래의 핵심 기술이지 ☀️\n\n태양전지 효율을 높이는 방법이나 태양광 발전 원리가 궁금해? 정말 흥미로운 분야야!\n\n실제로 실험해볼 수 있는 프로젝트들도 많아. 한번 연구계획을 세워볼까? 🔋';
    } else {
      // 🔧 위험한 주제인 경우 실험계획 관련 멘트 제거
      if (isDangerous) {
        return networkPrefix + `흥미로운 주제네! 🤔

과학에서 ${userInput}과 관련된 원리들은 정말 흥미로워. 하지만 안전을 위해 이론적으로만 접근하는 게 좋을 것 같아!

더 궁금한 점이 있으면 언제든 물어봐! ✨`;
      } else {
        return networkPrefix + '흥미로운 주제네! 🤔\n\n과학에서 궁금한 건 뭐든 물어봐. 실험으로 확인해볼 수 있는 건지도 같이 생각해보자!\n\n혹시 구체적으로 어떤 분야에 관심있어? 화학, 생물, 물리, 환경과학... 뭐든 좋아! ✨';
      }
    }
  }, [connectionStatus, currentResearchTopic]);

const triggerCompetitionSearch = useCallback(async () => {
    setIsLoading(true);
    
    try {
      let response = '';
      
      if (!isExcelLoaded) {
        response = `🔍 **실제 대회 출품작 데이터베이스를 검색해드려요!**

⏳ **Excel 파일 로딩 중...**
ISEF 국제대회와 국내 대회 데이터를 읽어오고 있어요!

${excelLoadError ? `❌ 로딩 오류: ${excelLoadError}` : '📊 잠시만 기다려주세요...'}

완료되면 실제 20,000+ 프로젝트에서 AI 번역 검색해드릴게요! 🔎

---
📊 대상: ISEF 국제대회 Excel + 국내DB Excel 파일`;
        
        startTypingMessage(response);
        setIsLoading(false);
        return;
      }

      if (messages.length <= 2) {
        // 🔥 AI 번역 기능을 강조한 초기 안내
        response = `🔍 **실제 대회 출품작 데이터베이스를 검색해드려요!**

실제 ISEF 국제대회와 국내 대회에서 수상한 프로젝트들의 정보를 찾아볼 수 있어요! 📚

🤖 **NEW! AI 자동 번역 검색 시스템:**
- **한글로 검색** → AI가 자동으로 영어 키워드로 번역해서 검색
- **영어 제목** → AI가 자연스러운 한글로 번역해서 표시  
- **무한 확장** → 새로운 과학 용어도 실시간 AI 번역 지원

**보유 데이터:**
🌍 ISEF 국제대회: ${realExcelData.isef.length.toLocaleString()}개 수상작
🇰🇷 국내 대회: ${realExcelData.domestic.length}개 수상작 (2023년)
📈 총 ${(realExcelData.isef.length + realExcelData.domestic.length).toLocaleString()}개 검증된 프로젝트

**🔥 AI 번역 검색 예시:**
- **"미세플라스틱"** → microplastic, plastic pollution으로 자동 검색
- **"양자컴퓨팅"** → quantum computing, quantum algorithm으로 확장 검색
- **"합성생물학"** → synthetic biology, bioengineering으로 지능형 검색

💡 **사용법:** 한글 과학 용어를 자연스럽게 입력하면 AI가 알아서 번역해서 해외 프로젝트까지 검색해드려요!

🔒 **개인정보 보호:** 연구자 이름은 마스킹 처리되어 표시됩니다 (홍** / A**)

---
🤖 AI 번역 지원 데이터베이스 ${(realExcelData.isef.length + realExcelData.domestic.length).toLocaleString()}개 기준`;

      } else {
        // 🔥 실제 AI 번역 검색 수행
        const latestMessage = messages[messages.length - 1].content;      
        // 키워드 추출 - 맥락 우선 접근: currentResearchTopic 활용
        const searchKeyword = currentResearchTopic || 
                             latestMessage.split(' ').find(word => word.length > 2) || 
                             '과학';        
        // 🔥 AI 번역 검색 실행 (비동기)
        const searchResults = await searchRealExcelData(searchKeyword);
        
        if (searchResults.totalResults === 0) {
  response = `🔍 **"${searchKeyword}" 검색 결과**

**검색 범위:**
- ISEF 국제대회: ${realExcelData.isef.length.toLocaleString()}개 출품작
- 국내 대회: ${realExcelData.domestic.length}개 출품작

**결과:** 해당 키워드와 일치하는 출품작을 찾지 못했습니다.

---
📊 검색 완료`;
        } else {
          // 🔥 개선된 출품작 중심 응답 생성
          response = `🔍 **"${searchKeyword}" 관련 출품작/수상작 검색 결과입니다.**

🎯 **이 검색된 자료들은 과학연구설계 AI에 탑재된 실제데이터입니다! 연구 주제 참고 자료로 활용해주세요. 

🤖 **AI 번역 확장 (참고용):**
- AI 추정 번역: ${searchResults.translationInfo?.mainTranslation}
- AI 생성 관련어: ${searchResults.translationInfo?.relatedTerms?.slice(0, 3).join(', ')}
- 키워드 확장: ${searchResults.translationInfo?.searchTermsUsed?.length || 0}개 (AI 번역 포함)

📊 **참가 통계:**
- ISEF 관련 출품작: ${searchResults.searchStats?.isefTotal || 0}개 발견
- 국내 관련 출품작: ${searchResults.searchStats?.domesticTotal || 0}개 발견

`;

          // ISEF 결과 표시 (출품작 중심)
          if (searchResults.international.length > 0) {
            response += `🌍 **ISEF 국제대회 출품작**`;
            
            if (searchResults.searchStats?.isefTruncated) {
              response += ` (연관성 상위 3개, 총 ${searchResults.searchStats.isefTotal}개 중)`;
            } else {
              response += ` (전체 ${searchResults.searchStats.isefTotal}개)`;
            }
            
            response += `:\n`;
            
            searchResults.international.forEach((item, index) => {
              response += `\n**${index + 1}. 키워드 일치도 ${item.relevanceScore?.toFixed(1)}점**\n`;
              response += `**📋 프로젝트:** "${item.title}"\n`;
              
              if (item.translatedTitle && item.translatedTitle !== "번역 처리 중..." && item.translatedTitle !== "번역 실패") {
                response += `**🇰🇷 한글 번역:** ${item.translatedTitle}\n`;
              }
              
              response += `**📅 ${item.year}년 ISEF ${item.category}분야 출품**\n`;
              
              if (item.author && item.author !== '작성자 정보 없음') {
                response += `**👨‍🔬 연구자:** ${item.author}\n`; // 이미 마스킹된 이름
              }
              
              if (item.country) {
                response += `**🌍 출신:** ${item.country}${item.state ? ` (${item.state})` : ''}\n`;
              }
              
              // 🔥 수상 정보는 부가 정보로 표시
              if (item.awards && item.awards !== '수상내역 없음' && item.awards.trim() !== '') {
                response += `**🏆 부가정보:** ${item.awards}\n`;
              }
            });
            
            if (searchResults.searchStats?.isefTruncated) {
              response += `\n💡 *더 많은 관련 출품작이 있습니다. 더 구체적인 키워드로 검색하면 정확한 결과를 볼 수 있어요!*\n`;
            }
          } else {
            response += `❌ **ISEF 국제대회:** 관련 출품작 없음\n`;
          }

          // 국내 결과 표시 (출품작 중심)
          if (searchResults.domestic.length > 0) {
            response += `\n🇰🇷 **국내 대회 출품작**`;
            
            if (searchResults.searchStats?.domesticTruncated) {
              response += ` (연관성 상위 3개, 총 ${searchResults.searchStats.domesticTotal}개 중)`;
            } else {
              response += ` (전체 ${searchResults.searchStats.domesticTotal}개)`;
            }
            
            response += `:\n`;
            
            searchResults.domestic.forEach((item, index) => {
              response += `\n**${index + 1}. 키워드 일치도 ${item.relevanceScore?.toFixed(1)}점**\n`;
              response += `**📋 프로젝트:** "${item.title}"\n`;
              response += `**📅 ${item.year}년 ${item.competition} 출품**\n`;
              
              if (item.author && item.author.trim() !== '') {
                response += `**👨‍🔬 연구자:** ${item.author}\n`; // 이미 마스킹된 이름
              }
              
              // 🔥 수상 정보는 부가 정보로 표시
              if (item.award && item.award !== '수상내역 없음' && item.award.trim() !== '') {
                response += `**🏆 부가정보:** ${item.award}\n`;
              }
            });
            
            if (searchResults.searchStats?.domesticTruncated) {
              response += `\n💡 *더 많은 관련 출품작이 있습니다. 더 구체적인 키워드로 검색하면 정확한 결과를 볼 수 있어요!*\n`;
            }
          } else {
            response += `\n❌ **국내 대회:** 관련 출품작 없음\n`;
          }

          response += `\n🎯 **활용 방법:**
- 출품 주제 아이디어 참고
- 연구 접근 방식 벤치마킹 
- 분야별 트렌드 파악
- 실험 설계 방향성 참고

🔒 **개인정보 보호:**
- 연구자 이름 마스킹 처리 (홍** / A**)
- 개인 식별 정보 보호

💡 **더 구체적인 검색:**
- 세부 기술명 사용 (예: "CRISPR", "quantum dot")
- 영어 키워드 직접 입력
- 여러 키워드 조합 검색

💡 **키워드 일치도:** 제목/분야명에서 검색어 포함 정도를 점수화 (단순 텍스트 매칭)

---
📊 AI 검색 완료: 키워드 연관성 기반 출품작 탐색`;
        }
      }
      
      startTypingMessage(response);
      
    } catch (error) {
      console.error("AI 번역 검색 오류:", error);
      
      const fallbackResponse = `❌ **AI 번역 검색 중 오류 발생**

번역 시스템에 일시적인 문제가 있어요.

**임시 해결책:**
- 영어 키워드로 직접 검색해보세요
- 잠시 후 다시 시도해보세요
- 새로고침 후 재시도

---
📊 AI 번역 시스템 일시 중단`;
      
      startTypingMessage(fallbackResponse);
    }
    
    setIsLoading(false);
  }, [messages, isExcelLoaded, realExcelData, excelLoadError, searchRealExcelData, startTypingMessage]);

  // 연구계획서 생성
  const triggerResearchPlan = useCallback(async () => {
    // PDF용 문구 정의
    const disclaimer = `
<div style="margin-top: 40px; padding: 30px; border: 2px solid #ddd; background-color: #f9f9f9; text-align: center; page-break-before: always; font-family: Arial, sans-serif;">
  <h3 style="color: #333; margin-bottom: 20px;">📋 이용 안내</h3>
  <p style="margin: 10px 0; line-height: 1.6; font-size: 14px;">이 자료는 참고용(학습·지도용) 예시이며, 그대로 제출하면 안 됩니다.</p>
  <p style="margin: 10px 0; line-height: 1.6; font-size: 14px;">본 자료는 실험 방법·데이터 분석·보고서 구성 방식을 학습하기 위한 '예시 자료'입니다.</p>
  <p style="margin: 10px 0; line-height: 1.6; font-size: 14px;">학교 제출용·대회 제출용 보고서를 대신 작성해주지 않습니다.</p>
  <p style="margin: 10px 0; line-height: 1.6; font-size: 14px; font-weight: bold; color: #d63384;">모든 작성 책임은 구매자 본인에게 있습니다.</p>
</div>
`;

    // 🔧 AI 기반 주제 전환 감지
    if (currentResearchTopic && currentResearchTopic.includes("(이론만)")) {
      const recentMessages = messages.slice(-4).map(msg => msg.content).join(' ');
      const transitionAnalysis = await checkTopicTransition(recentMessages);
      
      if (transitionAnalysis.hasTransitioned) {
        console.log("🔄 AI 감지: 안전한 대안 실험으로 전환!", transitionAnalysis);
        
        // 🔧 안전한 주제로 업데이트 (이론만 플래그 제거)
        setCurrentResearchTopic(transitionAnalysis.newSafeTopic);
        
        // 🔧 버튼 다시 활성화
        setPlanningButtonEnabled(true);
        setReportButtonEnabled(true);
        
        console.log("✅ 주제 업데이트:", transitionAnalysis.newSafeTopic);
      }
    }
    
    // 위험한 주제인 경우에만 생성 차단
    if (currentResearchTopic && currentResearchTopic.includes("(이론만)")) {
      const safetyMessage = `이 주제는 안전상의 이유로 실험계획서를 생성할 수 없어! ⚠️

대신 이론적으로 더 깊이 알아보거나, 관련된 안전한 실험 주제로 바꿔보는 건 어때?

예를 들어:
• 관련 원리를 시뮬레이션으로 관찰
• 비슷하지만 안전한 대체 실험
• 해당 분야의 기초 이론 탐구

어떤 방향으로 접근해볼까? 🤔`;
      
      startTypingMessage(safetyMessage);
      return;
    }
    
    setDocumentMode('research-plan');
    setIsDocumentGenerating(true);
    setDocumentProgress(0);
    setDocumentProgressMessage('🔬 연구계획서 생성을 시작합니다...');
    
    const messageContent = '🔬 연구계획서를 생성하고 있습니다...\n\n우측 문서 창에서 실시간으로 작성되는 모습을 확인해보세요! AI가 대화 맥락을 분석해서 맞춤형 연구계획을 만들어드릴게요! ✨\n\n💡 **문서를 보면서 궁금한 점이 생기면 언제든 저에게 물어보세요!** 예를 들어:\n• "실험 방법 2단계가 이해 안 되는데 더 자세히 설명해줘"\n• "준비물 중에서 대체할 수 있는 재료 있어?"\n• "이 실험에서 주의할 점이 더 있을까?"\n\n이렇게 구체적으로 질문하시면 더 상세한 답변을 드릴 수 있어요!';
    startTypingMessage(messageContent);
    
    // 진행률 단계들
    const progressSteps = [
      { progress: 3, message: '📚 대화 맥락 분석 중...', time: 4800 },
      { progress: 8, message: '🎯 연구 목적 설정 중...', time: 6000 },
      { progress: 15, message: '🔬 실험 설계 구성 중...', time: 5000 },
      { progress: 23, message: '📊 데이터 분석 계획 수립 중...', time: 5200 },
      { progress: 32, message: '📝 재료 및 준비물 정리 중...', time: 5000 },
      { progress: 42, message: '⚗️ 실험 절차 상세화 중...', time: 5500 },
      { progress: 52, message: '📈 예상 결과 분석 중...', time: 5000 },
      { progress: 62, message: '🔍 안전수칙 검토 중...', time: 4000 },
      { progress: 71, message: '📄 문서 구조화 시작...', time: 5600 },
      { progress: 79, message: '🎨 HTML 스타일링 적용 중...', time: 8000 },
      { progress: 86, message: '✨ 내용 검증 및 교정 중...', time: 5200 },
      { progress: 92, message: '🔧 최종 포맷팅 중...', time: 8000 },
      { progress: 97, message: '🎯 품질 최종 검토 중...', time: 6000 }
    ];
    
    let stepIndex = 0;
    const executeProgressStep = () => {
      if (stepIndex < progressSteps.length) {
        const currentStep = progressSteps[stepIndex];
        setDocumentProgress(currentStep.progress);
        setDocumentProgressMessage(currentStep.message);
        stepIndex++;
        
        if (stepIndex < progressSteps.length) {
          setTimeout(executeProgressStep, currentStep.time);
        }
      }
    };

    executeProgressStep();
    
    try {
      const document = await generateAdvancedDocument('research-plan');
      setDocumentProgress(100);
      setDocumentProgressMessage('✅ 연구계획서 생성 완료!');
      setTimeout(() => {
        setDocumentContent(document + disclaimer);
        setIsDocumentGenerating(false);
      }, 500);
    } catch (error) {
      const fallbackDocument = generateComprehensiveDocument('research-plan');
      setDocumentProgress(100);
      setDocumentProgressMessage('✅ 연구계획서 생성 완료!');
      setTimeout(() => {
        setDocumentContent(fallbackDocument + disclaimer);
        setIsDocumentGenerating(false);
      }, 500);
    }
    
    setReportButtonEnabled(true);
  }, [currentResearchTopic, messages, checkTopicTransition, startTypingMessage, generateAdvancedDocument, generateComprehensiveDocument]);

  // 실험보고서 생성
  const triggerExperimentReport = useCallback(async () => {
    // PDF용 문구 정의
    const disclaimer = `
<div style="margin-top: 40px; padding: 30px; border: 2px solid #ddd; background-color: #f9f9f9; text-align: center; page-break-before: always; font-family: Arial, sans-serif;">
  <h3 style="color: #333; margin-bottom: 20px;">📋 이용 안내</h3>
  <p style="margin: 10px 0; line-height: 1.6; font-size: 14px;">이 자료는 참고용(학습·지도용) 예시이며, 그대로 제출하면 안 됩니다.</p>
  <p style="margin: 10px 0; line-height: 1.6; font-size: 14px;">본 자료는 실험 방법·데이터 분석·보고서 구성 방식을 학습하기 위한 '예시 자료'입니다.</p>
  <p style="margin: 10px 0; line-height: 1.6; font-size: 14px;">학교 제출용·대회 제출용 보고서를 대신 작성해주지 않습니다.</p>
  <p style="margin: 10px 0; line-height: 1.6; font-size: 14px; font-weight: bold; color: #d63384;">모든 작성 책임은 구매자 본인에게 있습니다.</p>
</div>
`;

    // 🔧 AI 기반 주제 전환 감지
    if (currentResearchTopic && currentResearchTopic.includes("(이론만)")) {
      const recentMessages = messages.slice(-4).map(msg => msg.content).join(' ');
      const transitionAnalysis = await checkTopicTransition(recentMessages);
      
      if (transitionAnalysis.hasTransitioned) {
        console.log("🔄 AI 감지: 안전한 대안 실험으로 전환! (보고서)", transitionAnalysis);
        
        // 🔧 안전한 주제로 업데이트 (이론만 플래그 제거)
        setCurrentResearchTopic(transitionAnalysis.newSafeTopic);
        
        // 🔧 버튼 다시 활성화
        setPlanningButtonEnabled(true);
        setReportButtonEnabled(true);
        
        console.log("✅ 주제 업데이트 (보고서):", transitionAnalysis.newSafeTopic);
      }
    }
    
    // 위험한 주제인 경우에만 생성 차단  
    if (currentResearchTopic && currentResearchTopic.includes("(이론만)")) {
      const safetyMessage = `안전상의 이유로 이 주제의 실험보고서는 생성할 수 없어! 🛡️

하지만 이론적 분석 보고서나 안전한 대체 실험은 가능해!

• 이론적 연구 보고서
• 문헌 조사 보고서  
• 안전한 관련 실험 설계

어떤 형태로 정리해보고 싶어? 📚`;
      
      startTypingMessage(safetyMessage);
      return;
    }
    
    // 기존 연구계획서가 있으면 미리 캡처 (state 변경 전에 저장)
    const savedResearchPlan = (documentMode === 'research-plan' && documentContent) ? documentContent : null;

    setDocumentMode('experiment-report');
    setIsDocumentGenerating(true);
    setDocumentProgress(0);
    setDocumentProgressMessage('📝 실험보고서 생성을 시작합니다...');

    const messageContent = '📝 실험보고서를 생성하고 있습니다...\n\n우측 문서 창에서 가상의 실험 데이터와 함께 완성되는 보고서를 확인해보세요! 실제 과학 논문 형태로 작성해드릴게요! 📊\n\n💡 **보고서 내용 중 이해가 어려운 부분이 있으면 바로 질문하세요!** 예를 들어:\n• "실험 결과에서 변화율 95.7%가 뭘 의미해?"\n• "결론 부분을 더 쉽게 설명해줘"\n• "이 실험을 실제로 하려면 뭘 조심해야 해?"\n\n구체적으로 물어보실수록 정확하고 자세한 설명을 받을 수 있어요!';
    startTypingMessage(messageContent);
    
    // 실험보고서용 progressSteps
    const progressSteps = [
      { progress: 2, message: '🎯 실험 목적 분석 중...', time: 5000 },
      { progress: 6, message: '🧪 실험 방법 구성 중...', time: 3800 },
      { progress: 12, message: '📊 가상 데이터 생성 준비 중...', time: 3600 },
      { progress: 19, message: '🔢 실험 데이터 계산 중...', time: 4400 },
      { progress: 27, message: '📈 결과 분석 시작...', time: 5000 },
      { progress: 36, message: '📊 그래프 및 차트 생성 중...', time: 5000 },
      { progress: 45, message: '💡 결론 및 토의 작성 중...', time: 5600 },
      { progress: 54, message: '📚 참고문헌 정리 중...', time: 5600 },
      { progress: 63, message: '🔍 오차 분석 검토 중...', time: 4800 },
      { progress: 72, message: '📄 보고서 구조화 중...', time: 6400 },
      { progress: 80, message: '🎨 문서 포맷팅 적용 중...', time: 5600 },
      { progress: 87, message: '✨ 내용 검증 및 교정 중...', time: 7200 },
      { progress: 93, message: '🔧 최종 품질 검토 중...', time: 7400 },
      { progress: 98, message: '🎯 마무리 작업 중...', time: 3000 }
    ];
    
    let stepIndex = 0;
    const executeProgressStep = () => {
      if (stepIndex < progressSteps.length) {
        const currentStep = progressSteps[stepIndex];
        setDocumentProgress(currentStep.progress);
        setDocumentProgressMessage(currentStep.message);
        stepIndex++;
        
        if (stepIndex < progressSteps.length) {
          setTimeout(executeProgressStep, currentStep.time);
        }
      }
    };
    
    executeProgressStep();
    
    try {
      const document = await generateAdvancedDocument('experiment-report', savedResearchPlan);
      setDocumentProgress(100);
      setDocumentProgressMessage('✅ 실험보고서 생성 완료!');
      setTimeout(() => {
        setDocumentContent(document + disclaimer);
        setIsDocumentGenerating(false);
      }, 1000);
    } catch (error) {
      const fallbackDocument = generateComprehensiveDocument('experiment-report');
      setDocumentProgress(100);
      setDocumentProgressMessage('✅ 실험보고서 생성 완료!');
      setTimeout(() => {
        setDocumentContent(fallbackDocument + disclaimer);
        setIsDocumentGenerating(false);
      }, 1000);
    }
  }, [currentResearchTopic, messages, checkTopicTransition, startTypingMessage, generateAdvancedDocument, generateComprehensiveDocument, documentMode, documentContent]);

  // PDF 다운로드 함수 - 전체 문서 출력 수정
  const handlePDFDownload = useCallback(() => {
    console.log("📄 PDF 다운로드 시작!");
    
    // 1. 스타일 태그 생성 - 긴 문서 대응
    const printStyle = document.createElement('style');
    printStyle.id = 'pdf-print-style';
    printStyle.textContent = `
      @media print {
        .pdf-hide-sidebar { display: none !important; }
        .pdf-hide-chat { display: none !important; }
        .pdf-full-document { 
          width: 100vw !important; 
          height: auto !important;
          max-height: none !important;
          position: static !important; 
          top: auto !important; 
          left: auto !important; 
          z-index: auto !important;
          border: none !important;
          margin: 0 !important;
          padding: 20px !important;
          overflow: visible !important;
        }
        .pdf-full-document > div {
          height: auto !important;
          max-height: none !important;
          overflow: visible !important;
        }
        .pdf-full-document .overflow-y-auto {
          overflow: visible !important;
          height: auto !important;
          max-height: none !important;
        }
        body { margin: 0 !important; padding: 0 !important; }
        * { 
          -webkit-print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
      }
      
      @media screen {
        .pdf-hide-sidebar { display: none !important; }
        .pdf-hide-chat { display: none !important; }
        .pdf-full-document { 
          width: 100vw !important; 
          height: 100vh !important;
          position: fixed !important; 
          top: 0 !important; 
          left: 0 !important; 
          z-index: 9999 !important;
          border: none !important;
          margin: 0 !important;
          padding: 0 !important;
          overflow: auto !important;
        }
      }
    `;
    document.head.appendChild(printStyle);
    
    // 2. 요소 찾기 및 클래스 추가
    const sidebar = document.querySelector('div.w-56');
    const chatArea = document.querySelector('div.flex-1') || 
                     document.querySelector('[class*="flex-1"]') ||
                     document.querySelector('div[class*="w-2/3"]');
    const docArea = document.querySelector('div[class*="w-1/3"]') ||
                    document.querySelector('[class*="border-l"]');
    
    console.log("찾은 요소들:", { sidebar, chatArea, docArea });
    
    // 3. 클래스 추가로 숨기기/보이기
    if (sidebar) {
      sidebar.classList.add('pdf-hide-sidebar');
      console.log("✅ 사이드바 숨김");
    }
    
    if (chatArea) {
      chatArea.classList.add('pdf-hide-chat');
      console.log("✅ 채팅영역 숨김");
    }
    
    if (docArea) {
      docArea.classList.add('pdf-full-document');
      console.log("✅ 문서영역 전체화면");
      
      // 🔧 추가: 내부 스크롤 컨테이너들도 수정
      const scrollContainers = docArea.querySelectorAll('.overflow-y-auto, [style*="overflow-y"]');
      scrollContainers.forEach(container => {
        container.style.overflow = 'visible';
        container.style.height = 'auto';
        container.style.maxHeight = 'none';
      });
    }
    
    // 4. 잠시 기다린 후 인쇄
    setTimeout(() => {
      console.log("🖨️ 프린트 실행!");
      window.print();
      
      // 5. 인쇄 후 복구
      setTimeout(() => {
        console.log("🔄 화면 복구 시작");
        
        if (sidebar) sidebar.classList.remove('pdf-hide-sidebar');
        if (chatArea) chatArea.classList.remove('pdf-hide-chat');
        if (docArea) {
          docArea.classList.remove('pdf-full-document');
          
          // 🔧 스크롤 컨테이너 복구
          const scrollContainers = docArea.querySelectorAll('.overflow-y-auto, [class*="overflow"]');
          scrollContainers.forEach(container => {
            container.style.overflow = '';
            container.style.height = '';
            container.style.maxHeight = '';
          });
        }
        
        // 스타일 태그 제거
        const styleEl = document.getElementById('pdf-print-style');
        if (styleEl) styleEl.remove();
        
        console.log("✅ 화면 복구 완료!");
      }, 1000);
    }, 500);
  }, []);

  // 전체 리셋 함수
  const resetAllData = useCallback(() => {
    setMessages([
      {
        role: 'assistant',
        content: '안녕! 과학 실험이나 연구 아이디어 있으면 편하게 말해봐! 🧪\n\n어떤 분야에 관심있어? 화학, 생물, 물리, 환경과학... 뭐든 좋아!',
        timestamp: new Date().toLocaleTimeString(),
        isComplete: true
      }
    ]);
    setInputValue('');
    setIsLoading(false);
    setCurrentResearchTopic(null);
    setResearchStage('exploration');
    setConversationDepth(0);
    setPlanningButtonEnabled(false);
    setReportButtonEnabled(false);
    setTypingMessageIndex(null);
    setDisplayedText('');
    setDocumentMode(null);
    setIsDocumentGenerating(false);
    setDocumentContent('');
    setDocumentProgress(0);
    setDocumentProgressMessage('');
    setConnectionStatus('connected');
    setRetryCount(0);
    
    setTokenUsage({
      chatMessages: 0,
      systemPrompts: 0,
      generatedDocuments: 0,
      contextOverhead: 0,
      apiCallCount: 0
    });
  }, []);

  // 메시지 전송 - 개선된 이용권 체크
  const sendMessage = useCallback(async () => {
    if (!inputValue.trim() || isLoading) return;

    // 🆕 개선된 이용권 만료 체크
    if (licenseInfo?.isExpired || userStatus === 'expired') {
      const expiredMessage = {
        role: 'assistant',
        content: '⏰ 이용권이 만료되었습니다. 계속 사용하시려면 새로운 액세스 코드를 입력해주세요.',
        timestamp: new Date().toLocaleTimeString(),
        isComplete: true
      };
      setMessages(prev => [...prev, expiredMessage]);
      return;
    }

    // 🆕 무료체험 시간 체크
    if (userStatus === 'trial' && timeLeft <= 0) {
      handleTrialExpired();
      return;
    }

    const userMessage = {
      role: 'user',
      content: inputValue,
      timestamp: new Date().toLocaleTimeString()
    };

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    
    const currentInput = inputValue;
    setInputValue('');
    setIsLoading(true);

    // 🆕 연결 상태가 안 좋으면 먼저 체크
    if (connectionStatus === 'disconnected') {
      const isConnected = await checkConnectionStatus();
      if (!isConnected) {
        const fallbackResponse = generateFallbackResponse(currentInput);
        startTypingMessage(fallbackResponse);
        setIsLoading(false);
        return;
      }
    }

    // 트리거 상태 업데이트
    const shouldContinue = await updateTriggerStates(currentInput);
    
    if (!shouldContinue) {
      setIsLoading(false);
      return;
    }

    try {
      const conversationHistory = messages.map(msg => ({
        role: msg.role === 'assistant' ? 'assistant' : 'user',
        content: msg.content
      }));

      const response = await callClaudeAPI(conversationHistory, currentInput);
      
      // 연구 주제 자동 설정
      if (currentInput.includes('미세플라스틱')) {
        setCurrentResearchTopic('미세플라스틱 분해');
      } else if (currentInput.includes('다이오드')) {
        setCurrentResearchTopic('다이오드 논리회로');
      } else if (currentInput.includes('식물')) {
        setCurrentResearchTopic('식물 성장 연구');
      } else if (currentInput.includes('태양')) {
        setCurrentResearchTopic('태양에너지 연구');
      }

      startTypingMessage(response);
    } catch (error) {
      console.error("메시지 전송 오류:", error);
      
      // 🆝 재시도 카운트 증가
      setRetryCount(prev => prev + 1);
      
      const fallbackResponse = retryCount >= 3 ? 
        "네트워크 연결에 문제가 계속되고 있어요 🌐\n\n잠시 후 다시 시도해보거나, 페이지를 새로고침해주세요!" :
        "잠시 네트워크 상태를 확인해주세요 🌐\n\n연결이 불안정해서 응답할 수 없어요. 잠시 후 다시 시도해보세요!";
      
      startTypingMessage(fallbackResponse);
    }
    
    setIsLoading(false);
  }, [inputValue, isLoading, licenseInfo, userStatus, timeLeft, handleTrialExpired, messages, connectionStatus, checkConnectionStatus, generateFallbackResponse, startTypingMessage, updateTriggerStates, callClaudeAPI, retryCount]);

  // ===== useEffect 훅들 =====

  // 타이핑 애니메이션 효과
  useEffect(() => {
    if (typingMessageIndex !== null) {
      const message = messages[typingMessageIndex];
      if (message && !message.isComplete) {
        const fullText = message.content;
        const speed = 15; // 🔧 30ms → 15ms로 2배 빠르게 수정
        
        if (displayedText.length < fullText.length) {
          const timer = setTimeout(() => {
            setDisplayedText(fullText.slice(0, displayedText.length + 1));
          }, speed);
          return () => clearTimeout(timer);
        } else {
          // 타이핑 완료
          setMessages(prev => prev.map((msg, idx) => 
            idx === typingMessageIndex ? { ...msg, isComplete: true } : msg
          ));
          setTypingMessageIndex(null);
          setDisplayedText('');
        }
      }
    }
  }, [typingMessageIndex, displayedText, messages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, displayedText, scrollToBottom]);

  // 🔥 컴포넌트 마운트시 Excel 데이터 로딩
  useEffect(() => {
    if (isAuthenticated) {
      loadRealExcelData();
    }
  }, [loadRealExcelData, isAuthenticated]);

  // 타이머 카운트다운 (통합 버전)
useEffect(() => {
  if (isTimerActive && timeLeft > 0) {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        const newTime = prev - 1;
        
        if (newTime <= 0) {
          setIsTimerActive(false);
          
          // licenseInfo 업데이트 (기존 두 번째 useEffect 기능 통합)
          if (licenseInfo) {
            setLicenseInfo(prevInfo => prevInfo ? {
              ...prevInfo,
              isExpired: true,
              status: 'expired',
              timeLeft: 0
            } : null);
          }
          
          // 무료체험 만료 처리
          if (licenseInfo?.type === 'trial') {
            handleTrialExpired();
          }
          
          return 0;
        }
        return newTime;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }
}, [isTimerActive, timeLeft, licenseInfo, handleTrialExpired]);

  // 세션 상태 주기적 확인 (개선된 버전)
  useEffect(() => {
    if (isAuthenticated && sessionInfo) {
      // 🔥 무료체험(0000)은 백엔드 체크 안 함
      if (sessionInfo.code === '0000') {
        console.log('🎯 무료체험 - 백엔드 세션 체크 스킵');
        return;
      }
      
      console.log('💳 유료 코드 - 주기적 세션 체크 시작');
      
      // 즉시 1회 체크
      checkSession();
      
      // 30초마다 정기 체크 (캐시 고려)
      const interval = setInterval(() => {
        console.log('🔄 정기 세션 체크 실행');
        checkSession();
      }, 30000);
      
      return () => {
        console.log('🛑 세션 체크 인터벌 정리');
        clearInterval(interval);
      };
    }
  }, [isAuthenticated, sessionInfo, checkSession]);
  
 
  // 🆕 연결 상태 주기적 체크
  useEffect(() => {
    checkConnectionStatus();
    const interval = setInterval(checkConnectionStatus, 30000); // 30초마다 체크
    return () => clearInterval(interval);
  }, [checkConnectionStatus]);

  // ===== 컴포넌트 상수들 =====

  const quickTopics = [
    { icon: Atom, text: "미세플라스틱 분해", category: "화학" },
    { icon: Dna, text: "식물 성장 실험", category: "생물" },
    { icon: Globe, text: "태양전지 효율", category: "물리" },
    { icon: BarChart3, text: "다이오드 논리회로", category: "전자" }
  ];

  const researchStages = {
    exploration: { label: "주제 탐색", color: "bg-blue-100 text-blue-800" },
    planning: { label: "연구 설계", color: "bg-yellow-100 text-yellow-800" },
    execution: { label: "실험 진행", color: "bg-orange-100 text-orange-800" },
    writing: { label: "논문 작성", color: "bg-green-100 text-green-800" }
  };

  // ===== 로그인 화면 컴포넌트 =====
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          {/* 로고 및 제목 */}
          <div className="text-center mb-8">
            <div className="bg-blue-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
              <Beaker className="w-10 h-10 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Little Science AI Chat</h1>
            <p className="text-gray-600 text-sm">과학연구설계 AI 튜터</p>
          </div>

          {/* 액세스 코드 입력 */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                액세스 코드 입력
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                  placeholder="코드를 입력하세요"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg font-mono tracking-wider"
                  disabled={isVerifying}
                  maxLength={10}
                />
                <Key className="absolute right-3 top-3 w-5 h-5 text-gray-400" />
              </div>
            </div>

            {authError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center">
                  <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
                  <span className="text-red-700 text-sm">{authError}</span>
                </div>
              </div>
            )}

            <button
              onClick={() => verifyAccessCode(accessCode)}
              disabled={!accessCode.trim() || isVerifying}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center"
            >
              {isVerifying ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  검증 중...
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  접속하기
                </>
              )}
            </button>
          </div>

          {/* 무료체험 및 이용권 안내 */}
          <div className="mt-6 space-y-4">
            {/* 무료체험 안내 */}
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <h3 className="text-sm font-medium text-green-800 mb-2 flex items-center">
                🎉 무료체험 안내
              </h3>
              <p className="text-xs text-green-700 mb-2">
                코드 <span className="font-mono bg-green-100 px-1 rounded">0000</span>을 입력하면 6분간 무료로 체험할 수 있어요!
              </p>
              <ul className="text-xs text-green-600 space-y-1">
                <li>• 연구계획서 자동 생성</li>
                <li>• 실험보고서 AI 작성</li>
                <li>• 대회 출품작 AI 검색</li>
                <li>• 무제한 과학 상담</li>
              </ul>
            </div>
            
            {/* 프리미엄 안내 */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-sm font-medium text-blue-800 mb-2 flex items-center">
                ⭐ 프리미엄 이용권
              </h3>
              <div className="text-xs text-blue-700 space-y-1">
                <p>• <strong>일 단위 이용권:</strong> 1일, 7일, 30일 등</p>
                <p>• <strong>시간 단위 이용권:</strong> 60분, 180분 등</p>
                <p>• <strong>무제한 이용권:</strong> 영구 사용 가능</p>
              </div>
            </div>
          </div>

          {/* 연결 상태 */}
          <div className="mt-4 flex items-center justify-center text-xs text-gray-500">
            {connectionStatus === 'connected' ? (
              <><Wifi className="w-3 h-3 mr-1 text-green-500" /><span>서버 연결됨</span></>
            ) : (
              <><WifiOff className="w-3 h-3 mr-1 text-red-500" /><span>연결 확인 중...</span></>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ===== 메인 인터페이스 (로그인 후 화면) =====
  return (
    <div className="flex h-screen bg-gray-50">
      {/* 좌측 사이드바 - 연구도구 */}
      <div className="w-56 bg-white border-r border-gray-200 shadow-sm flex-shrink-0">
          <div className="p-4 border-b border-gray-100">
            <h2 className="text-lg font-bold text-gray-800 flex items-center">
              <TestTube className="w-5 h-5 mr-2 text-blue-600" />
              연구도구
            </h2>
            <p className="text-sm text-gray-500 mt-1">대화가 깊어지면 활성화됩니다</p>
            
            {/* 연결 상태 표시 */}
            <div className="flex items-center mt-2 text-xs">
              {connectionStatus === 'connected' ? (
                <><Wifi className="w-3 h-3 mr-1 text-green-500" /><span className="text-green-600">온라인</span></>
              ) : connectionStatus === 'unstable' ? (
                <><WifiOff className="w-3 h-3 mr-1 text-yellow-500" /><span className="text-yellow-600">불안정</span></>
              ) : (
                <><WifiOff className="w-3 h-3 mr-1 text-red-500" /><span className="text-red-600">오프라인</span></>
              )}
            </div>
          </div>
          
          {/* 🆕 개선된 사용자 상태 표시 */}
          <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex items-center space-x-2 mb-2">
              <User className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-800">
                {licenseInfo?.displayName || 
                 (userStatus === 'trial' ? '무료체험' : 
                  userStatus === 'premium' ? '프리미엄' : 
                  userStatus === 'unlimited' ? '무제한' : '게스트')}
              </span>
              {licenseInfo?.statusIcon && (
                <span className="text-sm">{licenseInfo.statusIcon}</span>
              )}
            </div>
            
            {/* 시간 표시 */}
            {licenseInfo && (
              <div className="space-y-2">
                {licenseInfo.type !== 'unlimited' && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">
                      {licenseInfo.isExpired ? '만료됨' : '남은 시간'}
                    </span>
                    <span className={`text-sm font-mono font-bold ${
                      licenseInfo.isExpired ? 'text-red-600' : 
                      timeLeft <= 60 ? 'text-red-600' : 
                      timeLeft <= 300 ? 'text-yellow-600' : 'text-blue-600'
                    }`}>
                      {formatTimeLeft(timeLeft)}
                    </span>
                  </div>
                )}
                
                {/* 프로그레스 바 */}
                {licenseInfo.type === 'trial' && !licenseInfo.isExpired && (
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${
                        timeLeft <= 60 ? 'bg-red-500' : timeLeft <= 180 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${Math.max((timeLeft / 360) * 100, 0)}%` }}
                    ></div>
                  </div>
                )}
                
                {/* 만료 시간 표시 */}
                {licenseInfo.expireTime && licenseInfo.type !== 'unlimited' && (
                  <div className="text-xs text-gray-500">
                    {licenseInfo.isExpired ? '만료됨: ' : '만료 예정: '}
                    {licenseInfo.expireTime.toLocaleDateString('ko-KR', {
                      month: 'short', 
                      day: 'numeric', 
                      hour: '2-digit', 
                      minute: '2-digit'
                    })}
                  </div>
                )}
                
                {/* 무제한 이용권 표시 */}
                {licenseInfo.type === 'unlimited' && (
                  <div className="text-xs text-green-600 flex items-center">
                    <Clock className="w-3 h-3 mr-1" />
                    <span>영구 이용 가능</span>
                  </div>
                )}
              </div>
            )}
            
            {/* 폴백: licenseInfo가 없는 경우 */}
            {!licenseInfo && userStatus === 'trial' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">남은 시간</span>
                  <span className={`text-sm font-mono font-bold ${timeLeft <= 60 ? 'text-red-600' : 'text-blue-600'}`}>
                    {formatTimeLeft(timeLeft)}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                      timeLeft <= 60 ? 'bg-red-500' : timeLeft <= 180 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.max((timeLeft / 360) * 100, 0)}%` }}
                  ></div>
                </div>
              </div>
            )}
            
            {!licenseInfo && userStatus === 'premium' && (
              <div className="text-xs text-green-600 flex items-center">
                <Clock className="w-3 h-3 mr-1" />
                <span>프리미엄 사용 중</span>
              </div>
            )}
          </div>
          
          <div className="p-4 space-y-3">
            {/* 연구계획 구체화 버튼 */}
            <button
              onClick={triggerResearchPlan}
              disabled={!planningButtonEnabled || isLoading || calculateUsage() >= 100 || documentMode === 'research-plan'}
              className={`w-full p-3 rounded-lg text-left transition-all duration-300 ${
                planningButtonEnabled && documentMode !== 'research-plan' && calculateUsage() < 100
                  ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-md transform hover:scale-105' 
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              <div className="flex items-center space-x-3">
                <FileText className="w-5 h-5" />
                <div>
                  <div className="font-medium">🔬 연구계획</div>
                  <div className="text-xs opacity-80">구체화하기</div>
                </div>
              </div>
              {planningButtonEnabled && documentMode !== 'research-plan' && calculateUsage() < 100 && (
                <div className="text-xs mt-2 opacity-90">
                  💡 실험 설계를 도와드려요!
                </div>
              )}
            </button>

            {/* 가상 실험보고서 버튼 */}
            <button
              onClick={triggerExperimentReport}
              disabled={!reportButtonEnabled || isLoading || calculateUsage() >= 100 || documentMode === 'experiment-report'}
              className={`w-full p-3 rounded-lg text-left transition-all duration-300 ${
                reportButtonEnabled && documentMode !== 'experiment-report' && calculateUsage() < 100
                  ? 'bg-green-500 hover:bg-green-600 text-white shadow-md transform hover:scale-105' 
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              <div className="flex items-center space-x-3">
                <BarChart3 className="w-5 h-5" />
                <div>
                  <div className="font-medium">📝 실험보고서</div>
                  <div className="text-xs opacity-80">가상 작성</div>
                </div>
              </div>
              {reportButtonEnabled && documentMode !== 'experiment-report' && calculateUsage() < 100 && (
                <div className="text-xs mt-2 opacity-90">
                  📊 논문 형태로 정리해드려요!
                </div>
              )}
            </button>

            {/* 대회 출품작 조회 버튼 */}
            <button
              onClick={triggerCompetitionSearch}
              disabled={isLoading || calculateUsage() >= 100}
              className={`w-full p-3 rounded-lg text-left transition-all duration-300 ${
                !isLoading && calculateUsage() < 100
                  ? 'bg-purple-500 hover:bg-purple-600 text-white shadow-md transform hover:scale-105' 
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              <div className="flex items-center space-x-3">
                <FlaskConical className="w-5 h-5" />
                <div>
                  <div className="font-medium">🏆 대회 출품작</div>
                  <div className="text-xs opacity-80">주제 참고 검색</div>
                </div>
              </div>
              {!isLoading && calculateUsage() < 100 && (
                <div className="text-xs mt-2 opacity-90">
                  🔍 ISEF·국내대회 AI 검색!
                </div>
              )}
            </button>

            {/* 다시 시작하기 버튼 */}
            <button
              onClick={resetAllData}
              disabled={isLoading}
              className={`w-full p-3 rounded-lg text-left transition-all duration-300 ${
                !isLoading
                  ? 'bg-gray-500 hover:bg-gray-600 text-white shadow-md transform hover:scale-105' 
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              <div className="flex items-center space-x-3">
                <div className="w-5 h-5 flex items-center justify-center">🔄</div>
                <div>
                  <div className="font-medium">다시 시작하기</div>
                  <div className="text-xs opacity-80">모든 대화 리셋</div>
                </div>
              </div>
            </button>

            {/* 로그아웃 버튼 */}
            <button
              onClick={handleLogout}
              className="w-full p-3 rounded-lg text-left transition-all duration-300 bg-red-500 hover:bg-red-600 text-white shadow-md transform hover:scale-105"
            >
              <div className="flex items-center space-x-3">
                <LogOut className="w-5 h-5" />
                <div>
                  <div className="font-medium">로그아웃</div>
                  <div className="text-xs opacity-80">세션 종료</div>
                </div>
              </div>
            </button>
          </div>

          {/* 진행 상황 표시 */}
          {currentResearchTopic && (
            <div className="p-4 border-t border-gray-100">
              <div className="text-sm font-medium text-gray-700 mb-2">현재 연구주제</div>
              <div className="text-xs text-gray-600 mb-2">{currentResearchTopic}</div>
              <span className={`px-2 py-1 rounded text-xs font-medium ${researchStages[researchStage].color}`}>
                {researchStages[researchStage].label}
              </span>
            </div>
          )}

          {/* 토큰 사용량 표시 */}
          <div className="p-4 border-t border-gray-100">
            <div className="text-sm text-gray-600 mb-2">대화 기억량</div>
            <div className="flex space-x-1">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className={`h-2 w-8 rounded ${
                    i < Math.ceil(calculateUsage() / 20) ? getUsageColor().replace('bg-', 'bg-') : 'bg-gray-200'
                  }`}
                ></div>
              ))}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {calculateUsage() < 20 ? '과학 주제로 대화해보세요' : 
               calculateUsage() < 60 ? '연구 주제가 명확해지고 있어요' : 
               calculateUsage() < 80 ? '깊이 있는 대화가 진행중이에요!' :
               '토큰 한계에 가까워지고 있어요'}
            </div>
            
            {/* 상세 토큰 사용량 표시 */}
            <div className="text-xs text-gray-400 mt-2 space-y-1">
              <div>데이타량: {Math.round(getCurrentTokens()).toLocaleString()}/{maxTokens.toLocaleString()}</div>
              <div>AI호출: {tokenUsage.apiCallCount}회</div>
              {retryCount > 0 && (
                <div className="text-yellow-600">재시도: {retryCount}회</div>
              )}
            </div>
          </div>

          {/* 🆕 Excel 로딩 상태 표시 */}
          {excelLoadError && (
            <div className="p-4 border-t border-gray-100">
              <div className="text-sm text-red-600 mb-1">Excel 데이터 상태</div>
              <div className="text-xs text-red-500">
                ❌ 로딩 실패
              </div>
              <div className="text-xs text-gray-500 mt-1">
                대회 검색 기능 일시 제한
              </div>
            </div>
          )}
        </div>

      {/* 중앙 채팅 영역 */}
      <div className={`flex flex-col transition-all duration-500 ${documentMode ? 'w-2/3' : 'flex-1'}`}>
          {/* 헤더 */}
          <div className="bg-white border-b border-gray-200 p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Beaker className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-800">과학연구설계 AI 튜터</h1>
                  <p className="text-sm text-gray-500">함께 연구하는 과학 멘토</p>
                </div>
              </div>
              
              {/* 🆕 개선된 상단 사용자 정보 및 타이머 */}
              <div className="flex items-center space-x-4">
                {/* 이용권 정보 */}
                {licenseInfo && (
                  <div className={`flex items-center space-x-2 px-3 py-1 rounded-full ${
                    licenseInfo.isExpired ? 'bg-red-50' : 
                    licenseInfo.type === 'unlimited' ? 'bg-green-50' : 
                    licenseInfo.type === 'trial' ? 'bg-orange-50' : 'bg-blue-50'
                  }`}>
                    {licenseInfo.type !== 'unlimited' && !licenseInfo.isExpired && (
                      <Clock className={`w-4 h-4 ${
                        timeLeft <= 60 ? 'text-red-600' : 
                        timeLeft <= 300 ? 'text-orange-600' : 'text-blue-600'
                      }`} />
                    )}
                    
                    {licenseInfo.type === 'unlimited' && (
                      <span className="text-lg">♾️</span>
                    )}
                    
                    <div className="flex flex-col">
                      <span className={`text-sm font-medium ${
                        licenseInfo.isExpired ? 'text-red-600' : 
                        licenseInfo.type === 'unlimited' ? 'text-green-600' : 
                        licenseInfo.type === 'trial' ? 'text-orange-600' : 'text-blue-600'
                      }`}>
                        {licenseInfo.displayName}
                      </span>
                      
                      {licenseInfo.type !== 'unlimited' && (
                        <span className={`text-xs font-mono ${
                          licenseInfo.isExpired ? 'text-red-500' : 
                          timeLeft <= 60 ? 'text-red-500' : 
                          timeLeft <= 300 ? 'text-orange-500' : 'text-blue-500'
                        }`}>
                          {formatTimeLeft(timeLeft)}
                        </span>
                      )}
                    </div>
                    
                    <span className="text-sm">{licenseInfo.statusIcon}</span>
                  </div>
                )}
                
                {/* 폴백: licenseInfo가 없는 경우 */}
                {!licenseInfo && userStatus === 'trial' && (
                  <div className="flex items-center space-x-2 bg-orange-50 px-3 py-1 rounded-full">
                    <Clock className="w-4 h-4 text-orange-600" />
                    <span className={`text-sm font-mono font-bold ${
                      timeLeft <= 60 ? 'text-red-600' : 'text-orange-600'
                    }`}>
                      {formatTimeLeft(timeLeft)}
                    </span>
                  </div>
                )}
                
                {!licenseInfo && userStatus === 'premium' && (
                  <div className="flex items-center space-x-2 bg-green-50 px-3 py-1 rounded-full">
                    <User className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-600">프리미엄</span>
                  </div>
                )}
                
                {!licenseInfo && userStatus === 'unlimited' && (
                  <div className="flex items-center space-x-2 bg-green-50 px-3 py-1 rounded-full">
                    <span className="text-lg">♾️</span>
                    <span className="text-sm text-green-600">무제한</span>
                  </div>
                )}
                
                {/* 연결 상태 */}
                <div className="flex items-center text-sm">
                  {connectionStatus === 'connected' ? (
                    <div className="flex items-center text-green-600">
                      <Wifi className="w-4 h-4 mr-1" />
                      <span>연결됨</span>
                    </div>
                  ) : (
                    <div className="flex items-center text-red-600">
                      <WifiOff className="w-4 h-4 mr-1" />
                      <span>오프라인</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 토큰 사용량 바 */}
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                <span>대화가 길어지면 새로 시작하기로 기억을 리셋해주세요</span>
                <span>{getCurrentTokens().toLocaleString()}/{maxTokens.toLocaleString()} ({Math.round(calculateUsage())}%)</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-300 ${getUsageColor()}`}
                  style={{ width: `${calculateUsage()}%` }}
                ></div>
              </div>
              {calculateUsage() > 80 && (
                <div className="flex items-center mt-1 text-xs text-red-600">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  <span>대화가 길어져서 곧 리셋이 필요해요!</span>
                </div>
              )}
            </div>
          </div>

          {/* 빠른 주제 버튼 */}
          {messages.length <= 2 && !documentMode && (
            <div className="p-4 bg-white border-b border-gray-100">
              <p className="text-sm text-gray-600 mb-3">관심 있는 분야를 클릭해보세요:</p>
              <div className="flex flex-wrap gap-2">
                {quickTopics.map((topic, index) => (
                  <button
                    key={index}
                    onClick={() => setInputValue(topic.text + " 실험해보고 싶어!")}
                    className="flex items-center space-x-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm transition-colors"
                  >
                    <topic.icon className="w-4 h-4" />
                    <span>{topic.text}</span>
                    <span className="text-xs text-gray-500">({topic.category})</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 메시지 영역 */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-3xl rounded-lg p-4 ${
                    message.role === 'user'
                      ? 'bg-blue-500 text-white'
                      : 'bg-white border border-gray-200'
                  }`}
                >
                  {message.role === 'assistant' && (
                    <div className="flex items-center space-x-2 mb-2">
                      {/* 🔧 수정된 부분: 대회 출품작 관련 메시지 감지 */}
                      {message.content.includes('연구계획서를 생성하고 있습니다') || 
                       message.content.includes('실험보고서를 생성하고 있습니다') ||
                       message.content.includes('실제 수상작들을 찾았어요') ||
                       message.content.includes('대회 출품작 데이터베이스를 검색해드려요') ||
                       message.content.includes('ISEF 국제대회와 국내 대회에서 수상한') ||
                       message.content.includes('실제 데이터베이스') ||
                       message.content.includes('AI 번역 검색') ? (
                        <>
                          <FileText className="w-4 h-4 text-purple-600" />
                          <span className="text-sm font-medium text-purple-600">과학튜터보조</span>
                        </>
                      ) : (
                        <>
                          <BookOpen className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-medium text-blue-600">과학 튜터</span>
                        </>
                      )}
                    </div>
                  )}
                  
                  <div className="whitespace-pre-wrap text-sm leading-relaxed">
                    {message.role === 'assistant' && index === typingMessageIndex && !message.isComplete 
                      ? displayedText 
                      : message.content}
                    {message.role === 'assistant' && index === typingMessageIndex && !message.isComplete && (
                      <span className="animate-pulse">|</span>
                    )}
                  </div>
                  
                  <div className="mt-2 text-xs opacity-70">
                    {message.timestamp}
                  </div>
                </div>
              </div>
            ))}            
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-lg p-4 max-w-xs">
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span className="text-sm text-gray-600">생각하는 중...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* 입력 영역 */}
          <div className="bg-white border-t border-gray-200 p-4">
            <div className="flex space-x-3">
              <div className="flex-1 relative">
                <textarea
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  placeholder="뭐든 궁금한 거 편하게 물어봐! (Shift+Enter로 줄바꿈)"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows="2"
                  disabled={isLoading || calculateUsage() >= 100 || licenseInfo?.isExpired || userStatus === 'expired' || (userStatus === 'trial' && timeLeft <= 0)}
                />
                
                {calculateUsage() >= 100 && (
                   <div className="absolute inset-0 bg-gray-100 bg-opacity-90 rounded-lg flex items-center justify-center">
                       <div className="text-center">
                          <p className="text-sm text-gray-700 font-medium">⏰ 대화가 너무 길어졌어요.</p>
                          <p className="text-xs text-gray-600 mt-1">왼쪽 사이드바에서 다시 시작하기 버튼을 눌러주세요.</p>
                       </div>
                   </div>
                 )}

                {(licenseInfo?.isExpired || userStatus === 'expired') && (
                  <div className="absolute inset-0 bg-gray-100 bg-opacity-90 rounded-lg flex items-center justify-center">
                    <p className="text-sm text-gray-600">이용권이 만료되었습니다</p>
                  </div>
                )}

                {userStatus === 'trial' && timeLeft <= 0 && !licenseInfo?.isExpired && (
                  <div className="absolute inset-0 bg-gray-100 bg-opacity-90 rounded-lg flex items-center justify-center">
                    <p className="text-sm text-gray-600">무료체험 시간이 만료되었습니다</p>
                  </div>
                )}
              </div>
              
              <button
                onClick={sendMessage}
                disabled={!inputValue.trim() || isLoading || calculateUsage() >= 100 || licenseInfo?.isExpired || userStatus === 'expired' || (userStatus === 'trial' && timeLeft <= 0)}
                className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white p-3 rounded-lg transition-colors flex items-center justify-center"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mt-2 text-xs text-gray-500 flex items-center justify-between">
              <span>💡 팁: 어떤 과학 주제든 AI가 자동 분석! 부적절한 주제는 필터링됩니다</span>
              <div className="flex items-center space-x-1">
                <Lightbulb className="w-3 h-3" />
                <span>Firebase Functions + AI 번역 + Excel DB</span>
              </div>
            </div>
          </div>
        </div>

      {/* 우측 문서 생성 영역 */}
      {documentMode && (
        <div className="w-1/3 bg-white border-l border-gray-200 flex flex-col min-h-0">
          {/* 문서 헤더 */}
          <div className="p-3 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {documentMode === 'research-plan' ? (
                  <FileText className="w-4 h-4 text-blue-600" />
                ) : (
                  <BarChart3 className="w-4 h-4 text-green-600" />
                )}
                <div>
                  <h3 className="font-bold text-gray-800 text-sm">
                    {documentMode === 'research-plan' ? '연구계획서' : '실험보고서'}
                  </h3>
                  <p className="text-xs text-gray-500">AI 실시간 문서 생성</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-1">
                {!isDocumentGenerating && documentContent && (
                  <button
                    onClick={() => handlePDFDownload()}
                    className="p-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors"
                    title="PDF 다운로드"
                  >
                    <Download className="w-3 h-3" />
                  </button>
                )}
                <button
                  onClick={() => {
                    setDocumentMode(null);
                    setIsDocumentGenerating(false);
                    setDocumentContent('');
                    setDocumentProgress(0);
                    setDocumentProgressMessage('');
                  }}
                  className="p-1.5 bg-red-500 hover:bg-red-600 text-white rounded transition-colors"
                  title="문서 닫기"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>

          {/* 문서 내용 */}
          <div className="flex-1 overflow-y-auto bg-white" style={{ minHeight: 0 }}>
            {isDocumentGenerating ? (
              <div className="flex flex-col items-center justify-center p-8">
                <div className="text-center max-w-sm">
                  {/* 원형 진행률 표시 */}
                  <div className="relative w-20 h-20 mx-auto mb-4">
                    <svg className="w-20 h-20 transform -rotate-90" viewBox="0 0 100 100">
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        stroke="#e5e7eb"
                        strokeWidth="8"
                        fill="none"
                      />
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        stroke="#3b82f6"
                        strokeWidth="8"
                        fill="none"
                        strokeDasharray={`${2 * Math.PI * 40}`}
                        strokeDashoffset={`${2 * Math.PI * 40 * (1 - documentProgress / 100)}`}
                        className="transition-all duration-500 ease-out"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-sm font-bold text-blue-600">{documentProgress}%</span>
                    </div>
                  </div>
                  
                  <p className="text-gray-700 font-medium mb-2 text-sm">{documentProgressMessage}</p>
                  <p className="text-xs text-gray-500">과학연구설계AI가 전문적인 문서를 작성하고 있습니다</p>
                  
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${documentProgress}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ) : documentContent ? (
              <div className="p-4">
                <div 
                  className="text-sm leading-relaxed"
                  style={{ 
                    fontSize: '13px', 
                    lineHeight: '1.5',
                    maxWidth: '100%',
                    wordWrap: 'break-word'
                  }}
                  dangerouslySetInnerHTML={{ __html: documentContent }}
                />
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}